import {
    AppstoreOutlined,
    CheckOutlined,
    CloudOutlined,
    CodeOutlined,
    DashboardOutlined,
    DatabaseOutlined,
    SolutionOutlined,
} from '@ant-design/icons';
import React from 'react';

export const smartformDetail = {
    title: 'SmartForms - Hệ thống quản lý form đa năng',
    description: () => (
        <>
            <h2 className="text-2xl font-bold mb-4">Tổng quan</h2>
            <p className="mb-4">
                SmartForms là một nền tảng quản lý form đa năng với khả năng tùy biến cao, phục vụ nhiều mục đích khác
                nhau cho các đối tượng người dùng khác nhau. Dự án này là đồ án tốt nghiệp của tôi, tập trung vào việc
                xây dựng một hệ thống toàn diện cho việc tạo, quản lý và phân tích dữ liệu từ nhiều loại form khác nhau.
            </p>
            <p className="mb-4">
                Hệ thống được thiết kế với <strong className="font-semibold">kiến trúc microservices</strong>, cho phép
                mở rộng dễ dàng và tích hợp các công nghệ hiện đại. Backend được xây dựng với Spring Boot, Spring
                Webflux, Spring Security và nhiều công nghệ Spring khác, trong khi frontend sử dụng ReactJS và Ant
                Design để tạo giao diện người dùng thân thiện và đáp ứng.
            </p>
            <p className="mb-4">
                SmartForms hỗ trợ nhiều loại form đa dạng từ thu thập thông tin, tạo đề thi, form bán hàng, điểm danh
                đến form phân cấp thứ bậc (AHP). Mỗi loại form đều có những tính năng riêng biệt phù hợp với mục đích sử
                dụng. Ngoài ra, hệ thống còn tích hợp các công nghệ hiện đại như nhận diện khuôn mặt, WebSocket, Kafka,
                FCM để cung cấp trải nghiệm người dùng tốt nhất.
            </p>

            <h3 className="text-xl font-semibold mb-3 mt-6">Kiến trúc Microservice của hệ thống</h3>
            <div className="mb-6">
                <img
                    src="/assets/images/projects/smartforms/kien-truc-microservice.png"
                    alt="Kiến trúc microservice của SmartForms"
                    className="w-full rounded-lg shadow-md"
                />
                <p className="text-sm text-gray-600 italic mt-2 text-center">
                    Kiến trúc microservice của hệ thống SmartForms
                </p>
            </div>
        </>
    ),
    problem: () => (
        <>
            <h2 className="text-2xl font-bold mb-4">Vấn đề cần giải quyết</h2>

            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-md mb-6">
                <p className="italic text-gray-700">
                    "Trong thời đại số hóa ngày nay, các form trực tuyến đã trở thành cầu nối quan trọng giữa người dùng
                    và hệ thống. Tuy nhiên, hầu hết các giải pháp hiện có đều đang hoạt động như những hòn đảo riêng
                    biệt, thiếu sự liên kết và toàn diện cần thiết để đáp ứng nhu cầu đa dạng của người dùng hiện đại."
                </p>
            </div>

            <p className="mb-6 text-gray-800">
                Sau thời gian nghiên cứu và đánh giá thực tế, tôi đã xác định năm thách thức chính trong các hệ thống
                quản lý form hiện có, những rào cản này không chỉ gây khó khăn cho người dùng mà còn hạn chế đáng kể
                tiềm năng ứng dụng của công nghệ form trong nhiều lĩnh vực:
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300 border-t-4 border-red-500">
                    <div className="p-5">
                        <div className="flex items-center mb-3">
                            <div className="w-10 h-10 flex-shrink-0 bg-red-100 rounded-full flex items-center justify-center mr-3">
                                <span className="text-red-600 font-bold">1</span>
                            </div>
                            <h3 className="font-bold text-lg text-gray-800">Thiếu tính đa dạng và tùy biến</h3>
                        </div>
                        <p className="text-gray-700 mb-3">
                            Hầu hết các nền tảng form hiện tại chỉ tập trung vào một loại form cụ thể, thiếu khả năng
                            đáp ứng nhiều nhu cầu khác nhau.
                        </p>
                        <div className="bg-red-50 p-3 rounded-md">
                            <p className="text-sm text-red-700">
                                <span className="font-semibold">Hệ quả:</span> Tổ chức và doanh nghiệp phải sử dụng
                                nhiều nền tảng khác nhau, gây ra sự phân mảnh dữ liệu, tăng chi phí vận hành và tạo ra
                                trải nghiệm không đồng nhất cho người dùng cuối.
                            </p>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300 border-t-4 border-yellow-500">
                    <div className="p-5">
                        <div className="flex items-center mb-3">
                            <div className="w-10 h-10 flex-shrink-0 bg-yellow-100 rounded-full flex items-center justify-center mr-3">
                                <span className="text-yellow-600 font-bold">2</span>
                            </div>
                            <h3 className="font-bold text-lg text-gray-800">Hạn chế về tính năng phân tích</h3>
                        </div>
                        <p className="text-gray-700 mb-3">
                            Các hệ thống hiện có thường thiếu khả năng phân tích dữ liệu sâu, đặc biệt với các form phức
                            tạp như đề thi hay form AHP.
                        </p>
                        <div className="bg-yellow-50 p-3 rounded-md">
                            <p className="text-sm text-yellow-700">
                                <span className="font-semibold">Hệ quả:</span> Người quản lý không thể khai thác hết giá
                                trị từ dữ liệu thu thập được, gây khó khăn trong việc đưa ra quyết định dựa trên dữ liệu
                                và đo lường hiệu quả của các hoạt động.
                            </p>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300 border-t-4 border-green-500">
                    <div className="p-5">
                        <div className="flex items-center mb-3">
                            <div className="w-10 h-10 flex-shrink-0 bg-green-100 rounded-full flex items-center justify-center mr-3">
                                <span className="text-green-600 font-bold">3</span>
                            </div>
                            <h3 className="font-bold text-lg text-gray-800">Thiếu tính năng bảo mật nâng cao</h3>
                        </div>
                        <p className="text-gray-700 mb-3">
                            Trong các tình huống nhạy cảm như thi cử hoặc thu thập thông tin cá nhân, các biện pháp bảo
                            mật thường không đầy đủ.
                        </p>
                        <div className="bg-green-50 p-3 rounded-md">
                            <p className="text-sm text-green-700">
                                <span className="font-semibold">Hệ quả:</span> Rủi ro lớn về lộ thông tin nhạy cảm, gian
                                lận trong thi cử, và khó đáp ứng các quy định ngày càng nghiêm ngặt về bảo vệ dữ liệu,
                                thông tin cá nhân.
                            </p>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300 border-t-4 border-blue-500">
                    <div className="p-5">
                        <div className="flex items-center mb-3">
                            <div className="w-10 h-10 flex-shrink-0 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                                <span className="text-blue-600 font-bold">4</span>
                            </div>
                            <h3 className="font-bold text-lg text-gray-800">
                                Không hỗ trợ đa dạng phương thức xác thực
                            </h3>
                        </div>
                        <p className="text-gray-700 mb-3">
                            Thiếu các phương pháp định danh người dùng như nhận diện khuôn mặt trong các trường hợp điểm
                            danh hoặc thi cử.
                        </p>
                        <div className="bg-blue-50 p-3 rounded-md">
                            <p className="text-sm text-blue-700">
                                <span className="font-semibold">Hệ quả:</span> Không thể đảm bảo tính xác thực của người
                                tham gia, dẫn đến các vấn đề như điểm danh hộ, gian lận thi cử và giảm độ tin cậy của dữ
                                liệu thu thập được.
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300 border-t-4 border-purple-500 mb-8">
                <div className="p-5">
                    <div className="flex items-center mb-3">
                        <div className="w-10 h-10 flex-shrink-0 bg-purple-100 rounded-full flex items-center justify-center mr-3">
                            <span className="text-purple-600 font-bold">5</span>
                        </div>
                        <h3 className="font-bold text-lg text-gray-800">
                            Thiếu tính năng thông báo và cập nhật thời gian thực
                        </h3>
                    </div>
                    <p className="text-gray-700 mb-3">
                        Người dùng thường không nhận được thông báo kịp thời khi có phản hồi mới hoặc thay đổi trong
                        form.
                    </p>
                    <div className="bg-purple-50 p-3 rounded-md">
                        <p className="text-sm text-purple-700">
                            <span className="font-semibold">Hệ quả:</span> Làm chậm quá trình phản hồi và tương tác, tạo
                            ra trải nghiệm không liền mạch cho người dùng và giảm hiệu quả của quy trình làm việc, đặc
                            biệt trong các tình huống cần phản ứng nhanh.
                        </p>
                    </div>
                </div>
            </div>

            <div className="bg-gray-50 p-5 rounded-lg border-l-4 border-indigo-500 mb-6">
                <h3 className="text-xl font-semibold text-gray-800 mb-3">Cơ hội đổi mới</h3>
                <p className="text-gray-700 mb-3">
                    Những thách thức này không chỉ là rào cản mà còn là cơ hội để tạo ra một giải pháp đột phá. Bằng
                    cách giải quyết đồng thời cả năm vấn đề này, một nền tảng mới có thể:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                    <li>Tạo ra trải nghiệm thống nhất và liền mạch cho người dùng cuối</li>
                    <li>Cung cấp cái nhìn toàn diện về dữ liệu để ra quyết định tốt hơn</li>
                    <li>Đảm bảo tuân thủ các quy định và tiêu chuẩn bảo mật hiện đại</li>
                    <li>Nâng cao tính xác thực và độ tin cậy của dữ liệu thu thập</li>
                    <li>Tối ưu hóa quy trình làm việc thông qua phản hồi thời gian thực</li>
                </ul>
            </div>

            <p className="text-gray-800 font-medium">
                Nhận thức rõ những thách thức và cơ hội này, tôi đã phát triển SmartForms với tầm nhìn tạo ra một nền
                tảng toàn diện và linh hoạt, có khả năng giải quyết đồng thời tất cả các vấn đề trên và mở ra khả năng
                ứng dụng trong nhiều lĩnh vực khác nhau.
            </p>
        </>
    ),
    solution: () => (
        <>
            <h2 className="text-2xl font-bold mb-4">Giải pháp đề xuất</h2>

            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-5 rounded-lg mb-8 border-l-4 border-blue-500">
                <p className="text-gray-800 mb-3">
                    <span className="font-semibold">SmartForms</span> không chỉ đơn thuần là một nền tảng quản lý form -
                    đây là một hệ sinh thái toàn diện được thiết kế đặc biệt để giải quyết những thách thức phức tạp
                    trong quản lý form hiện đại. Bằng cách kết hợp các công nghệ tiên tiến và kiến trúc linh hoạt,
                    SmartForms mang đến một giải pháp đột phá với 6 trụ cột chính:
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                {/* Hệ thống đa dạng loại form */}
                <div className="bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 overflow-hidden border border-gray-100">
                    <div className="bg-gradient-to-r from-purple-600 to-indigo-600 py-4 px-6">
                        <div className="flex items-center">
                            <span className="flex items-center justify-center w-10 h-10 rounded-full bg-white text-purple-600 font-bold text-lg mr-3">
                                1
                            </span>
                            <h3 className="text-xl font-bold text-white">Hệ thống đa dạng loại form</h3>
                        </div>
                    </div>
                    <div className="p-6">
                        <p className="text-gray-700 mb-4">
                            SmartForms vượt qua giới hạn của các nền tảng truyền thống bằng cách cung cấp năm loại form
                            chuyên biệt, mỗi loại được tối ưu hóa cho các mục đích sử dụng cụ thể:
                        </p>
                        <ul className="space-y-3">
                            <li className="flex items-start">
                                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-purple-100 flex items-center justify-center mr-3 mt-1">
                                    <span className="w-2 h-2 bg-purple-600 rounded-full"></span>
                                </span>
                                <div>
                                    <span className="font-semibold block text-gray-800">Form thu thập thông tin</span>
                                    <p className="text-sm text-gray-600">
                                        Hỗ trợ với 6 loại câu hỏi khác nhau, từ đơn giản đến phức tạp
                                    </p>
                                </div>
                            </li>
                            <li className="flex items-start">
                                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-purple-100 flex items-center justify-center mr-3 mt-1">
                                    <span className="w-2 h-2 bg-purple-600 rounded-full"></span>
                                </span>
                                <div>
                                    <span className="font-semibold block text-gray-800">Form đề thi thông minh</span>
                                    <p className="text-sm text-gray-600">
                                        Tích hợp ngân hàng câu hỏi và hệ thống chấm điểm tự động
                                    </p>
                                </div>
                            </li>
                            <li className="flex items-start">
                                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-purple-100 flex items-center justify-center mr-3 mt-1">
                                    <span className="w-2 h-2 bg-purple-600 rounded-full"></span>
                                </span>
                                <div>
                                    <span className="font-semibold block text-gray-800">Form bán hàng</span>
                                    <p className="text-sm text-gray-600">
                                        Tích hợp QR code và hệ thống quản lý đơn hàng
                                    </p>
                                </div>
                            </li>
                            <li className="flex items-start">
                                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-purple-100 flex items-center justify-center mr-3 mt-1">
                                    <span className="w-2 h-2 bg-purple-600 rounded-full"></span>
                                </span>
                                <div>
                                    <span className="font-semibold block text-gray-800">Form điểm danh thông minh</span>
                                    <p className="text-sm text-gray-600">
                                        Kết hợp nhận diện khuôn mặt và xác thực định danh
                                    </p>
                                </div>
                            </li>
                            <li className="flex items-start">
                                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-purple-100 flex items-center justify-center mr-3 mt-1">
                                    <span className="w-2 h-2 bg-purple-600 rounded-full"></span>
                                </span>
                                <div>
                                    <span className="font-semibold block text-gray-800">Form nhận diện</span>
                                    <p className="text-sm text-gray-600">
                                        Tích hợp nhận diện khuôn mặt để phân tích và hiển thị dữ liệu cá nhân tương ứng
                                    </p>
                                </div>
                            </li>
                            <li className="flex items-start">
                                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-purple-100 flex items-center justify-center mr-3 mt-1">
                                    <span className="w-2 h-2 bg-purple-600 rounded-full"></span>
                                </span>
                                <div>
                                    <span className="font-semibold block text-gray-800">
                                        Form phân cấp thứ bậc (AHP)
                                    </span>
                                    <p className="text-sm text-gray-600">
                                        Hỗ trợ phân tích quyết định đa tiêu chí chuyên sâu
                                    </p>
                                </div>
                            </li>
                        </ul>
                    </div>
                </div>

                {/* Kiến trúc microservices */}
                <div className="bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 overflow-hidden border border-gray-100">
                    <div className="bg-gradient-to-r from-blue-600 to-cyan-600 py-4 px-6">
                        <div className="flex items-center">
                            <span className="flex items-center justify-center w-10 h-10 rounded-full bg-white text-blue-600 font-bold text-lg mr-3">
                                2
                            </span>
                            <h3 className="text-xl font-bold text-white">Kiến trúc microservices</h3>
                        </div>
                    </div>
                    <div className="p-6">
                        <p className="text-gray-700 mb-4">
                            Nền tảng của SmartForms được xây dựng trên kiến trúc microservices hiện đại, mang lại nhiều
                            lợi thế vượt trội so với các hệ thống monolithic truyền thống:
                        </p>

                        <div className="grid grid-cols-1 gap-4 mb-4">
                            <div className="p-3 bg-blue-50 rounded-lg">
                                <h4 className="font-semibold text-blue-800 mb-1">Phân tách dịch vụ theo chức năng</h4>
                                <p className="text-sm text-gray-700">
                                    Mỗi microservice được thiết kế để thực hiện một chức năng riêng biệt, cho phép phát
                                    triển, triển khai và mở rộng độc lập.
                                </p>
                            </div>

                            <div className="p-3 bg-blue-50 rounded-lg">
                                <h4 className="font-semibold text-blue-800 mb-1">Khả năng mở rộng linh hoạt</h4>
                                <p className="text-sm text-gray-700">
                                    Hệ thống có thể dễ dàng mở rộng các dịch vụ cụ thể khi cần thiết, tối ưu hóa việc sử
                                    dụng tài nguyên và chi phí vận hành.
                                </p>
                            </div>

                            <div className="p-3 bg-blue-50 rounded-lg">
                                <h4 className="font-semibold text-blue-800 mb-1">Tính sẵn sàng cao</h4>
                                <p className="text-sm text-gray-700">
                                    Thiết kế phân tán giúp hệ thống duy trì hoạt động ngay cả khi một số dịch vụ gặp sự
                                    cố, đảm bảo trải nghiệm người dùng liền mạch.
                                </p>
                            </div>
                        </div>

                        <div className="border-t border-dashed border-gray-200 pt-4">
                            <p className="text-sm text-gray-700 italic">
                                <span className="font-semibold">Công nghệ sử dụng:</span> Spring Cloud Gateway, Eureka
                                Service Discovery, Spring Boot, Spring Webflux
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                {/* Bảo mật nâng cao */}
                <div className="bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 overflow-hidden border border-gray-100">
                    <div className="bg-gradient-to-r from-red-600 to-pink-600 py-3 px-4">
                        <div className="flex items-center">
                            <span className="flex items-center justify-center w-8 h-8 rounded-full bg-white text-red-600 font-bold text-base mr-2">
                                3
                            </span>
                            <h3 className="text-lg font-bold text-white">Bảo mật nâng cao</h3>
                        </div>
                    </div>
                    <div className="p-4">
                        <p className="text-gray-700 mb-3 text-sm">
                            Hệ thống bảo mật đa lớp của SmartForms được thiết kế để bảo vệ dữ liệu nhạy cảm và đảm bảo
                            tính toàn vẹn:
                        </p>

                        <ul className="space-y-2 mb-3">
                            <li className="flex items-start">
                                <span className="flex-shrink-0 w-4 h-4 rounded-full bg-red-100 flex items-center justify-center mr-2 mt-1">
                                    <span className="w-1.5 h-1.5 bg-red-600 rounded-full"></span>
                                </span>
                                <div>
                                    <span className="font-semibold text-sm block text-gray-800">
                                        Mã hóa AES với vector IV
                                    </span>
                                    <p className="text-xs text-gray-600">
                                        Bảo vệ dữ liệu với thuật toán mã hóa mạnh mẽ
                                    </p>
                                </div>
                            </li>
                            <li className="flex items-start">
                                <span className="flex-shrink-0 w-4 h-4 rounded-full bg-red-100 flex items-center justify-center mr-2 mt-1">
                                    <span className="w-1.5 h-1.5 bg-red-600 rounded-full"></span>
                                </span>
                                <div>
                                    <span className="font-semibold text-sm block text-gray-800">
                                        Kiểm tra toàn vẹn HMAC
                                    </span>
                                    <p className="text-xs text-gray-600">Ngăn chặn việc giả mạo hoặc sửa đổi dữ liệu</p>
                                </div>
                            </li>
                            <li className="flex items-start">
                                <span className="flex-shrink-0 w-4 h-4 rounded-full bg-red-100 flex items-center justify-center mr-2 mt-1">
                                    <span className="w-1.5 h-1.5 bg-red-600 rounded-full"></span>
                                </span>
                                <div>
                                    <span className="font-semibold text-sm block text-gray-800">
                                        Xác thực OAuth2 và JWT
                                    </span>
                                    <p className="text-xs text-gray-600">Quản lý phiên và ủy quyền an toàn</p>
                                </div>
                            </li>
                        </ul>
                    </div>
                </div>

                {/* Cập nhật thời gian thực */}
                <div className="bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 overflow-hidden border border-gray-100">
                    <div className="bg-gradient-to-r from-amber-600 to-orange-600 py-3 px-4">
                        <div className="flex items-center">
                            <span className="flex items-center justify-center w-8 h-8 rounded-full bg-white text-amber-600 font-bold text-base mr-2">
                                4
                            </span>
                            <h3 className="text-lg font-bold text-white">Cập nhật thời gian thực</h3>
                        </div>
                    </div>
                    <div className="p-4">
                        <p className="text-gray-700 mb-3 text-sm">
                            SmartForms tích hợp ba công nghệ thời gian thực để đảm bảo người dùng luôn được cập nhật với
                            thông tin mới nhất:
                        </p>

                        <ul className="space-y-2 mb-3">
                            <li className="flex items-start">
                                <span className="flex-shrink-0 w-4 h-4 rounded-full bg-amber-100 flex items-center justify-center mr-2 mt-1">
                                    <span className="w-1.5 h-1.5 bg-amber-600 rounded-full"></span>
                                </span>
                                <div>
                                    <span className="font-semibold text-sm block text-gray-800">WebSocket</span>
                                    <p className="text-xs text-gray-600">Cập nhật tức thì giữa máy khách và máy chủ</p>
                                </div>
                            </li>
                            <li className="flex items-start">
                                <span className="flex-shrink-0 w-4 h-4 rounded-full bg-amber-100 flex items-center justify-center mr-2 mt-1">
                                    <span className="w-1.5 h-1.5 bg-amber-600 rounded-full"></span>
                                </span>
                                <div>
                                    <span className="font-semibold text-sm block text-gray-800">Apache Kafka</span>
                                    <p className="text-xs text-gray-600">
                                        Xử lý tin nhắn đáng tin cậy giữa các services
                                    </p>
                                </div>
                            </li>
                            <li className="flex items-start">
                                <span className="flex-shrink-0 w-4 h-4 rounded-full bg-amber-100 flex items-center justify-center mr-2 mt-1">
                                    <span className="w-1.5 h-1.5 bg-amber-600 rounded-full"></span>
                                </span>
                                <div>
                                    <span className="font-semibold text-sm block text-gray-800">
                                        Firebase Cloud Messaging
                                    </span>
                                    <p className="text-xs text-gray-600">Thông báo đẩy trên nhiều thiết bị</p>
                                </div>
                            </li>
                        </ul>
                    </div>
                </div>

                {/* Tích hợp AI và phân tích dữ liệu */}
                <div className="bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 overflow-hidden border border-gray-100">
                    <div className="bg-gradient-to-r from-green-600 to-teal-600 py-3 px-4">
                        <div className="flex items-center">
                            <span className="flex items-center justify-center w-8 h-8 rounded-full bg-white text-green-600 font-bold text-base mr-2">
                                5
                            </span>
                            <h3 className="text-lg font-bold text-white">Tích hợp AI và phân tích</h3>
                        </div>
                    </div>
                    <div className="p-4">
                        <p className="text-gray-700 mb-3 text-sm">
                            SmartForms sử dụng công nghệ AI và các công cụ phân tích tiên tiến để tạo giá trị từ dữ
                            liệu:
                        </p>

                        <ul className="space-y-2 mb-3">
                            <li className="flex items-start">
                                <span className="flex-shrink-0 w-4 h-4 rounded-full bg-green-100 flex items-center justify-center mr-2 mt-1">
                                    <span className="w-1.5 h-1.5 bg-green-600 rounded-full"></span>
                                </span>
                                <div>
                                    <span className="font-semibold text-sm block text-gray-800">
                                        Nhận diện khuôn mặt
                                    </span>
                                    <p className="text-xs text-gray-600">Sử dụng DeepFace cho xác thực và điểm danh</p>
                                </div>
                            </li>
                            <li className="flex items-start">
                                <span className="flex-shrink-0 w-4 h-4 rounded-full bg-green-100 flex items-center justify-center mr-2 mt-1">
                                    <span className="w-1.5 h-1.5 bg-green-600 rounded-full"></span>
                                </span>
                                <div>
                                    <span className="font-semibold text-sm block text-gray-800">
                                        Công cụ phân tích phản hồi
                                    </span>
                                    <p className="text-xs text-gray-600">Trực quan hóa và phân tích dữ liệu thu thập</p>
                                </div>
                            </li>
                            <li className="flex items-start">
                                <span className="flex-shrink-0 w-4 h-4 rounded-full bg-green-100 flex items-center justify-center mr-2 mt-1">
                                    <span className="w-1.5 h-1.5 bg-green-600 rounded-full"></span>
                                </span>
                                <div>
                                    <span className="font-semibold text-sm block text-gray-800">
                                        Thống kê và báo cáo
                                    </span>
                                    <p className="text-xs text-gray-600">Dashboard và biểu đồ cho ra quyết định</p>
                                </div>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>

            {/* Quản lý tài nguyên */}
            <div className="bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 overflow-hidden border border-gray-100 mb-8">
                <div className="bg-gradient-to-r from-indigo-600 to-violet-600 py-4 px-6">
                    <div className="flex items-center">
                        <span className="flex items-center justify-center w-10 h-10 rounded-full bg-white text-indigo-600 font-bold text-lg mr-3">
                            6
                        </span>
                        <h3 className="text-xl font-bold text-white">Quản lý tài nguyên hiệu quả</h3>
                    </div>
                </div>
                <div className="p-6">
                    <p className="text-gray-700 mb-4">
                        SmartForms cung cấp một hệ thống quản lý tài nguyên toàn diện, tối ưu hóa việc tổ chức và truy
                        cập form:
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div className="p-3 bg-indigo-50 rounded-lg">
                            <h4 className="font-semibold text-indigo-800 mb-1">Tổ chức theo cấu trúc thư mục</h4>
                            <p className="text-sm text-gray-700">
                                Hệ thống thư mục phân cấp cho phép người dùng tổ chức form một cách logic và dễ quản lý.
                            </p>
                        </div>

                        <div className="p-3 bg-indigo-50 rounded-lg">
                            <h4 className="font-semibold text-indigo-800 mb-1">Chia sẻ và quản lý quyền</h4>
                            <p className="text-sm text-gray-700">
                                Kiểm soát chi tiết về quyền truy cập, cho phép chia sẻ an toàn với các cá nhân hoặc
                                nhóm.
                            </p>
                        </div>

                        <div className="p-3 bg-indigo-50 rounded-lg">
                            <h4 className="font-semibold text-indigo-800 mb-1">Đánh dấu sao mục quan trọng</h4>
                            <p className="text-sm text-gray-700">
                                Đánh dấu sao các form thường xuyên sử dụng để truy cập nhanh chóng.
                            </p>
                        </div>

                        <div className="p-3 bg-indigo-50 rounded-lg">
                            <h4 className="font-semibold text-indigo-800 mb-1">Thùng rác và khôi phục</h4>
                            <p className="text-sm text-gray-700">
                                Bảo vệ khỏi xóa nhầm với khả năng khôi phục dữ liệu từ thùng rác.
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Đặc điểm nổi bật */}
            <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-6 rounded-lg shadow-inner mb-6 border-l-4 border-blue-500">
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Đặc điểm nổi bật của giải pháp</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-white p-4 rounded-lg shadow-sm">
                        <div className="flex items-center mb-3">
                            <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="h-4 w-4 text-blue-600"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M5 13l4 4L19 7"
                                    />
                                </svg>
                            </div>
                            <span className="font-semibold text-gray-800">Toàn diện</span>
                        </div>
                        <p className="text-sm text-gray-600">
                            Giải pháp đáp ứng đầy đủ các nhu cầu từ thu thập dữ liệu, tạo đề thi, đến phân tích quyết
                            giám sát người làm bài.
                        </p>
                    </div>

                    <div className="bg-white p-4 rounded-lg shadow-sm">
                        <div className="flex items-center mb-3">
                            <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center mr-3">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="h-4 w-4 text-green-600"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                                    />
                                </svg>
                            </div>
                            <span className="font-semibold text-gray-800">Mở rộng</span>
                        </div>
                        <p className="text-sm text-gray-600">
                            Kiến trúc microservices cho phép dễ dàng thêm tính năng mới hoặc mở rộng quy mô.
                        </p>
                    </div>

                    <div className="bg-white p-4 rounded-lg shadow-sm">
                        <div className="flex items-center mb-3">
                            <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center mr-3">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="h-4 w-4 text-red-600"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                    />
                                </svg>
                            </div>
                            <span className="font-semibold text-gray-800">An toàn</span>
                        </div>
                        <p className="text-sm text-gray-600">
                            Bảo mật đa lớp bảo vệ dữ liệu nhạy cảm và ngăn chặn các nguy cơ bảo mật.
                        </p>
                    </div>
                </div>
            </div>

            <h2 className="text-2xl font-bold mt-8 mb-4">Đóng góp nổi bật</h2>
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-5 rounded-lg mb-8 border-l-4 border-blue-500">
                <p className="text-gray-800 mb-3">
                    Trong quá trình phát triển hệ thống SmartForms, tôi đã đề xuất và triển khai nhiều giải pháp kỹ
                    thuật để giải quyết các thách thức phức tạp. Dưới đây là 10 đóng góp nổi bật, thể hiện cách tiếp cận
                    sáng tạo và hiệu quả của dự án trong việc xây dựng một hệ thống quản lý form hiện đại, an toàn và có
                    khả năng mở rộng cao.
                </p>
                <p className="text-gray-800">
                    Mỗi giải pháp được trình bày theo cấu trúc rõ ràng, bao gồm vấn đề gặp phải, giải pháp đề xuất và
                    kết quả đạt được, giúp người đọc hiểu được giá trị và tác động của từng đóng góp đối với toàn bộ hệ
                    thống.
                </p>
            </div>

            <div className="space-y-6">
                <div className="bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 overflow-hidden border border-gray-100">
                    <div className="bg-gradient-to-r from-blue-600 to-cyan-600 py-4 px-6">
                        <div className="flex items-center">
                            <span className="flex items-center justify-center w-10 h-10 rounded-full bg-white text-blue-600 font-bold text-lg mr-3">
                                1
                            </span>
                            <h3 className="text-xl font-bold text-white">
                                Xây dựng hệ thống Microservice linh hoạt và có khả năng mở rộng
                            </h3>
                        </div>
                    </div>
                    <div className="p-6">
                        <div className="mb-4">
                            <h4 className="font-semibold text-gray-800 mb-2">Vấn đề gặp phải:</h4>
                            <p className="text-gray-700 mb-3">
                                Trong quá trình phát triển hệ thống SmartForms, cần đáp ứng nhu cầu đa dạng với nhiều
                                loại form khác nhau. Kiến trúc nguyên khối (monolithic) gây ra các vấn đề:
                            </p>
                            <ul className="space-y-2 mb-3">
                                <li className="flex items-start">
                                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center mr-3 mt-1">
                                        <span className="w-2 h-2 bg-blue-600 rounded-full"></span>
                                    </span>
                                    <span className="text-sm text-gray-700">
                                        Khó khăn trong bảo trì và nâng cấp do sự phụ thuộc chặt chẽ giữa các thành phần
                                    </span>
                                </li>
                                <li className="flex items-start">
                                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center mr-3 mt-1">
                                        <span className="w-2 h-2 bg-blue-600 rounded-full"></span>
                                    </span>
                                    <span className="text-sm text-gray-700">
                                        Khó mở rộng quy mô khi lưu lượng tăng, phải nâng cấp toàn bộ hệ thống
                                    </span>
                                </li>
                                <li className="flex items-start">
                                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center mr-3 mt-1">
                                        <span className="w-2 h-2 bg-blue-600 rounded-full"></span>
                                    </span>
                                    <span className="text-sm text-gray-700">
                                        Rủi ro cao khi một lỗi nhỏ có thể ảnh hưởng toàn bộ hệ thống
                                    </span>
                                </li>
                                <li className="flex items-start">
                                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center mr-3 mt-1">
                                        <span className="w-2 h-2 bg-blue-600 rounded-full"></span>
                                    </span>
                                    <span className="text-sm text-gray-700">
                                        Hạn chế khả năng áp dụng công nghệ mới phù hợp với từng chức năng
                                    </span>
                                </li>
                                <li className="flex items-start">
                                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center mr-3 mt-1">
                                        <span className="w-2 h-2 bg-blue-600 rounded-full"></span>
                                    </span>
                                    <span className="text-sm text-gray-700">
                                        Khó khăn trong phát triển song song giữa các nhóm phát triển
                                    </span>
                                </li>
                            </ul>
                        </div>

                        <div className="mb-4">
                            <h4 className="font-semibold text-gray-800 mb-2">Giải pháp:</h4>
                            <p className="text-gray-700 mb-3">
                                Chia nhỏ hệ thống thành các service độc lập, với mỗi service đảm nhiệm một chức năng
                                riêng biệt:
                            </p>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                <div className="p-3 bg-blue-50 rounded-lg">
                                    <h4 className="font-semibold text-blue-800 mb-1">Các Microservice chính</h4>
                                    <ul className="space-y-1 text-sm text-gray-700">
                                        <li>• API Gateway: Điểm truy cập duy nhất cho client, định tuyến yêu cầu</li>
                                        <li>• Folder Service: Quản lý thư mục, form, câu hỏi và câu trả lời</li>
                                        <li>• Exam Service: Quản lý kỳ thi trực tuyến, ngân hàng câu hỏi</li>
                                        <li>• Auth Service: Xác thực người dùng với JWT và OAuth 2.0</li>
                                        <li>• Notification Service: Gửi thông báo qua FCM của Google</li>
                                        <li>• Consumer Service: Xử lý message từ Kafka, lưu trữ và ghi log</li>
                                        <li>• Identify Service: Quản lý danh sách định danh, nhận diện khuôn mặt</li>
                                        <li>• Store Service: Quản lý thông tin cửa hàng, sản phẩm, đơn hàng</li>
                                    </ul>
                                </div>
                                <div className="p-3 bg-blue-50 rounded-lg">
                                    <h4 className="font-semibold text-blue-800 mb-1">Cơ chế giao tiếp</h4>
                                    <ul className="space-y-1 text-sm text-gray-700">
                                        <li>• RESTful API: Chuẩn giao tiếp giữa các service</li>
                                        <li>• Feign Client: Đơn giản hóa việc gọi RESTful API</li>
                                        <li>• Eureka Service Discovery: Đăng ký và lưu trữ thông tin service</li>
                                        <li>• Load Balancer: Phân phối tải giữa các instance của service</li>
                                        <li>• Spring Cloud Gateway: Định tuyến request đến service</li>
                                    </ul>
                                </div>
                            </div>

                            <div className="rounded-lg overflow-hidden border border-gray-200 mb-4">
                                <div className="bg-gray-50 p-3 border-b border-gray-200">
                                    <h5 className="font-medium text-gray-700">Cơ chế hoạt động chi tiết</h5>
                                </div>
                                <div className="p-3">
                                    <ol className="space-y-2 list-decimal list-inside text-sm text-gray-700">
                                        <li>Đăng ký Service: Các service khởi động và đăng ký với Eureka Server</li>
                                        <li>
                                            Truy vấn Service: Service cần sử dụng service khác thông qua Feign Client
                                        </li>
                                        <li>
                                            Phân giải tên Service: Feign Client truy vấn Eureka lấy thông tin instance
                                        </li>
                                        <li>Cân bằng tải: Load Balancer chọn instance phù hợp để xử lý request</li>
                                        <li>Gửi Request: Feign Client tạo và gửi HTTP request đến instance đã chọn</li>
                                        <li>Xử lý Request: Instance nhận và xử lý request, trả về response</li>
                                        <li>
                                            Cơ chế thử lại: Nếu instance gặp sự cố, tự động thử lại với instance khác
                                        </li>
                                    </ol>
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 gap-4 mb-4">
                            <div className="p-3 bg-blue-50 rounded-lg">
                                <h4 className="font-semibold text-blue-800 mb-1">Kết quả đạt được:</h4>
                                <ul className="space-y-2">
                                    <li className="flex items-start">
                                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center mr-3 mt-1">
                                            <span className="w-2 h-2 bg-blue-600 rounded-full"></span>
                                        </span>
                                        <span className="text-sm text-gray-700">
                                            Tính linh hoạt cao, dễ dàng thêm mới và nâng cấp từng service độc lập
                                        </span>
                                    </li>
                                    <li className="flex items-start">
                                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center mr-3 mt-1">
                                            <span className="w-2 h-2 bg-blue-600 rounded-full"></span>
                                        </span>
                                        <span className="text-sm text-gray-700">
                                            Khả năng mở rộng quy mô từng service dựa trên nhu cầu sử dụng thực tế
                                        </span>
                                    </li>
                                    <li className="flex items-start">
                                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center mr-3 mt-1">
                                            <span className="w-2 h-2 bg-blue-600 rounded-full"></span>
                                        </span>
                                        <span className="text-sm text-gray-700">
                                            Tái sử dụng code và dễ dàng bảo trì với các service độc lập, gắn kết cao
                                        </span>
                                    </li>
                                    <li className="flex items-start">
                                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center mr-3 mt-1">
                                            <span className="w-2 h-2 bg-blue-600 rounded-full"></span>
                                        </span>
                                        <span className="text-sm text-gray-700">
                                            Độc lập về công nghệ, cho phép lựa chọn công nghệ phù hợp nhất cho từng
                                            service
                                        </span>
                                    </li>
                                    <li className="flex items-start">
                                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center mr-3 mt-1">
                                            <span className="w-2 h-2 bg-blue-600 rounded-full"></span>
                                        </span>
                                        <span className="text-sm text-gray-700">
                                            Tăng cường khả năng phục hồi khi xảy ra lỗi, một service lỗi không ảnh hưởng
                                            toàn hệ thống
                                        </span>
                                    </li>
                                    <li className="flex items-start">
                                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center mr-3 mt-1">
                                            <span className="w-2 h-2 bg-blue-600 rounded-full"></span>
                                        </span>
                                        <span className="text-sm text-gray-700">
                                            Phát triển song song hiệu quả giữa các nhóm phát triển trên các service khác
                                            nhau
                                        </span>
                                    </li>
                                </ul>
                            </div>
                        </div>

                        <div className="border-t border-dashed border-gray-200 pt-4">
                            <p className="text-sm text-gray-700 italic">
                                <span className="font-semibold">Công nghệ sử dụng:</span> Spring Cloud Gateway, Eureka
                                Service Discovery, Spring Boot, Spring Webflux, Feign Client, Spring Cloud LoadBalancer
                            </p>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 overflow-hidden border border-gray-100">
                    <div className="bg-gradient-to-r from-purple-600 to-indigo-600 py-4 px-6">
                        <div className="flex items-center">
                            <span className="flex items-center justify-center w-10 h-10 rounded-full bg-white text-purple-600 font-bold text-lg mr-3">
                                2
                            </span>
                            <h3 className="text-xl font-bold text-white">Lấy danh sách tài nguyên được chia sẻ</h3>
                        </div>
                    </div>
                    <div className="p-6">
                        <div className="mb-4">
                            <h4 className="font-semibold text-gray-800 mb-2">Vấn đề gặp phải:</h4>
                            <p className="text-gray-700 mb-3">
                                Hệ thống SmartForms cho phép chia sẻ nhiều loại tài nguyên khác nhau (thư mục, biểu mẫu,
                                đề thi, khóa học,...), gây ra các thách thức:
                            </p>
                            <ul className="space-y-2 mb-3">
                                <li className="flex items-start">
                                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-purple-100 flex items-center justify-center mr-3 mt-1">
                                        <span className="w-2 h-2 bg-purple-600 rounded-full"></span>
                                    </span>
                                    <span className="text-sm text-gray-700">
                                        Bùng nổ số lượng bản ghi khi một tài nguyên được chia sẻ với nhiều người dùng
                                    </span>
                                </li>
                                <li className="flex items-start">
                                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-purple-100 flex items-center justify-center mr-3 mt-1">
                                        <span className="w-2 h-2 bg-purple-600 rounded-full"></span>
                                    </span>
                                    <span className="text-sm text-gray-700">
                                        Truy vấn phức tạp và kém hiệu quả qua nhiều bảng dữ liệu
                                    </span>
                                </li>
                                <li className="flex items-start">
                                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-purple-100 flex items-center justify-center mr-3 mt-1">
                                        <span className="w-2 h-2 bg-purple-600 rounded-full"></span>
                                    </span>
                                    <span className="text-sm text-gray-700">
                                        Khó khăn trong việc duy trì và mở rộng khi số lượng tài nguyên tăng
                                    </span>
                                </li>
                                <li className="flex items-start">
                                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-purple-100 flex items-center justify-center mr-3 mt-1">
                                        <span className="w-2 h-2 bg-purple-600 rounded-full"></span>
                                    </span>
                                    <span className="text-sm text-gray-700">
                                        Trải nghiệm người dùng kém do thời gian chờ đợi lâu
                                    </span>
                                </li>
                            </ul>
                        </div>

                        <div className="mb-4">
                            <h4 className="font-semibold text-gray-800 mb-2">Giải pháp:</h4>
                            <p className="text-gray-700 mb-3">
                                Kết hợp mô hình Observer, Kafka và Elasticsearch để tạo quy trình xử lý dữ liệu bất đồng
                                bộ, hiệu quả:
                            </p>

                            <div className="rounded-lg overflow-hidden border border-gray-200 mb-4">
                                <div className="bg-gray-50 p-3 border-b border-gray-200">
                                    <h5 className="font-medium text-gray-700">Luồng xử lý</h5>
                                </div>
                                <div className="p-3">
                                    <ol className="space-y-2 list-decimal list-inside text-sm text-gray-700">
                                        <li>
                                            Kích hoạt sự kiện (Observer Pattern) khi có thay đổi về tài nguyên được chia
                                            sẻ
                                        </li>
                                        <li>Publish thông tin sự kiện lên Kafka topic tương ứng (add/update/delete)</li>
                                        <li>Consumer Service tiêu thụ message và xử lý dữ liệu</li>
                                        <li>Lưu trữ và đánh chỉ mục thông tin trong Elasticsearch</li>
                                        <li>
                                            Truy vấn dữ liệu nhanh chóng từ Elasticsearch khi người dùng cần xem danh
                                            sách
                                        </li>
                                    </ol>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                <div className="p-3 bg-purple-50 rounded-lg">
                                    <h4 className="font-semibold text-purple-800 mb-1">Observer Pattern</h4>
                                    <p className="text-sm text-gray-700">
                                        Theo dõi sự kiện thay đổi (tạo, cập nhật, xóa) các tài nguyên có thể chia sẻ và
                                        thông báo cho các thành phần đã đăng ký.
                                    </p>
                                </div>
                                <div className="p-3 bg-purple-50 rounded-lg">
                                    <h4 className="font-semibold text-purple-800 mb-1">Kafka Message Queue</h4>
                                    <p className="text-sm text-gray-700">
                                        Đảm bảo các message được truyền đi tin cậy và hiệu quả với kiến trúc phân tán và
                                        khả năng xử lý thông lượng cao.
                                    </p>
                                </div>
                                <div className="p-3 bg-purple-50 rounded-lg">
                                    <h4 className="font-semibold text-purple-800 mb-1">Consumer Service</h4>
                                    <p className="text-sm text-gray-700">
                                        Xử lý các message từ Kafka, thực hiện các hành động tương ứng (thêm, cập nhật,
                                        xóa) và đẩy dữ liệu vào Elasticsearch.
                                    </p>
                                </div>
                                <div className="p-3 bg-purple-50 rounded-lg">
                                    <h4 className="font-semibold text-purple-800 mb-1">Elasticsearch</h4>
                                    <p className="text-sm text-gray-700">
                                        Lưu trữ và đánh chỉ mục thông tin chia sẻ dưới dạng document JSON, cho phép truy
                                        vấn nhanh chóng và hiệu quả.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 gap-4 mb-4">
                            <div className="p-3 bg-purple-50 rounded-lg">
                                <h4 className="font-semibold text-purple-800 mb-1">Kết quả đạt được:</h4>
                                <ul className="space-y-2">
                                    <li className="flex items-start">
                                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-purple-100 flex items-center justify-center mr-3 mt-1">
                                            <span className="w-2 h-2 bg-purple-600 rounded-full"></span>
                                        </span>
                                        <span className="text-sm text-gray-700">
                                            Hiệu suất truy vấn tối ưu, thời gian phản hồi gần như tức thì
                                        </span>
                                    </li>
                                    <li className="flex items-start">
                                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-purple-100 flex items-center justify-center mr-3 mt-1">
                                            <span className="w-2 h-2 bg-purple-600 rounded-full"></span>
                                        </span>
                                        <span className="text-sm text-gray-700">
                                            Giảm độ phức tạp của hệ thống, chỉ cần truy vấn trên một index duy nhất
                                        </span>
                                    </li>
                                    <li className="flex items-start">
                                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-purple-100 flex items-center justify-center mr-3 mt-1">
                                            <span className="w-2 h-2 bg-purple-600 rounded-full"></span>
                                        </span>
                                        <span className="text-sm text-gray-700">
                                            Khả năng mở rộng tuyệt vời khi lượng dữ liệu và người dùng tăng lên
                                        </span>
                                    </li>
                                    <li className="flex items-start">
                                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-purple-100 flex items-center justify-center mr-3 mt-1">
                                            <span className="w-2 h-2 bg-purple-600 rounded-full"></span>
                                        </span>
                                        <span className="text-sm text-gray-700">
                                            Dễ dàng thêm các loại tài nguyên mới có thể chia sẻ
                                        </span>
                                    </li>
                                    <li className="flex items-start">
                                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-purple-100 flex items-center justify-center mr-3 mt-1">
                                            <span className="w-2 h-2 bg-purple-600 rounded-full"></span>
                                        </span>
                                        <span className="text-sm text-gray-700">
                                            Tính nhất quán và độ tin cậy cao nhờ Kafka
                                        </span>
                                    </li>
                                </ul>
                            </div>
                        </div>

                        <div className="border-t border-dashed border-gray-200 pt-4">
                            <p className="text-sm text-gray-700 italic">
                                <span className="font-semibold">Công nghệ sử dụng:</span> Observer Pattern, Kafka,
                                Elasticsearch, Spring Boot, Kibana
                            </p>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 overflow-hidden border border-gray-100">
                    <div className="bg-gradient-to-r from-green-600 to-teal-600 py-4 px-6">
                        <div className="flex items-center">
                            <span className="flex items-center justify-center w-10 h-10 rounded-full bg-white text-green-600 font-bold text-lg mr-3">
                                3
                            </span>
                            <h3 className="text-xl font-bold text-white">
                                Giải pháp Ghi log API tập trung cho hệ thống Microservice
                            </h3>
                        </div>
                    </div>
                    <div className="p-6">
                        <div className="mb-4">
                            <h4 className="font-semibold text-gray-800 mb-2">Vấn đề gặp phải:</h4>
                            <p className="text-gray-700 mb-3">
                                Trong kiến trúc Microservice, log nằm rải rác trên nhiều service dẫn đến các khó khăn:
                            </p>
                            <ul className="space-y-2 mb-3">
                                <li className="flex items-start">
                                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-green-100 flex items-center justify-center mr-3 mt-1">
                                        <span className="w-2 h-2 bg-green-600 rounded-full"></span>
                                    </span>
                                    <span className="text-sm text-gray-700">
                                        Khó tổng hợp và theo dõi log phân tán trên nhiều service
                                    </span>
                                </li>
                                <li className="flex items-start">
                                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-green-100 flex items-center justify-center mr-3 mt-1">
                                        <span className="w-2 h-2 bg-green-600 rounded-full"></span>
                                    </span>
                                    <span className="text-sm text-gray-700">
                                        Không thể thống kê tần suất và lưu lượng sử dụng API
                                    </span>
                                </li>
                                <li className="flex items-start">
                                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-green-100 flex items-center justify-center mr-3 mt-1">
                                        <span className="w-2 h-2 bg-green-600 rounded-full"></span>
                                    </span>
                                    <span className="text-sm text-gray-700">
                                        Khó khăn trong triển khai tính năng tính phí API
                                    </span>
                                </li>
                                <li className="flex items-start">
                                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-green-100 flex items-center justify-center mr-3 mt-1">
                                        <span className="w-2 h-2 bg-green-600 rounded-full"></span>
                                    </span>
                                    <span className="text-sm text-gray-700">
                                        Quá trình debug và truy vết lỗi phức tạp, tốn thời gian
                                    </span>
                                </li>
                            </ul>
                        </div>

                        <div className="mb-4">
                            <h4 className="font-semibold text-gray-800 mb-2">Giải pháp:</h4>
                            <div className="mb-3">
                                <p className="text-gray-700 mb-3">
                                    Triển khai hệ thống ghi log API tập trung bằng cách kết hợp các công nghệ:
                                </p>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                    <div className="p-3 bg-green-50 rounded-lg">
                                        <h4 className="font-semibold text-green-800 mb-1">
                                            Custom Filter (Spring Boot)
                                        </h4>
                                        <p className="text-sm text-gray-700">
                                            Sử dụng LoggingFilter để thu thập thông tin chi tiết từ request, response và
                                            thời gian xử lý.
                                        </p>
                                    </div>
                                    <div className="p-3 bg-green-50 rounded-lg">
                                        <h4 className="font-semibold text-green-800 mb-1">Observer Pattern</h4>
                                        <p className="text-sm text-gray-700">
                                            Áp dụng mô hình Pub/Sub để xử lý sự kiện log một cách bất đồng bộ.
                                        </p>
                                    </div>
                                    <div className="p-3 bg-green-50 rounded-lg">
                                        <h4 className="font-semibold text-green-800 mb-1">Kafka</h4>
                                        <p className="text-sm text-gray-700">
                                            Sử dụng như message broker để truyền tải dữ liệu log đảm bảo tin cậy, bất
                                            đồng bộ.
                                        </p>
                                    </div>
                                    <div className="p-3 bg-green-50 rounded-lg">
                                        <h4 className="font-semibold text-green-800 mb-1">Elasticsearch</h4>
                                        <p className="text-sm text-gray-700">
                                            Lưu trữ, đánh index và tìm kiếm dữ liệu log hiệu quả, hỗ trợ phân tích.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="rounded-lg overflow-hidden border border-gray-200 mb-4">
                                <div className="bg-gray-50 p-3 border-b border-gray-200">
                                    <h5 className="font-medium text-gray-700">Luồng hoạt động ghi log</h5>
                                </div>
                                <div className="p-3">
                                    <ol className="space-y-2 list-decimal list-inside text-sm text-gray-700">
                                        <li>LoggingFilter thu thập thông tin chi tiết từ request và response</li>
                                        <li>Đóng gói thông tin vào đối tượng AggregateLog</li>
                                        <li>LogEventListener nhận sự kiện và gửi đến Kafka topic log-req-res</li>
                                        <li>Consumer Service lắng nghe topic và nhận dữ liệu log</li>
                                        <li>Dữ liệu log được lưu trữ và đánh index trong Elasticsearch</li>
                                    </ol>
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 gap-4 mb-4">
                            <div className="p-3 bg-green-50 rounded-lg">
                                <h4 className="font-semibold text-green-800 mb-1">Kết quả đạt được:</h4>
                                <ul className="space-y-2">
                                    <li className="flex items-start">
                                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-green-100 flex items-center justify-center mr-3 mt-1">
                                            <span className="w-2 h-2 bg-green-600 rounded-full"></span>
                                        </span>
                                        <span className="text-sm text-gray-700">
                                            Ghi log tập trung từ tất cả service trong hệ thống tại Elasticsearch
                                        </span>
                                    </li>
                                    <li className="flex items-start">
                                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-green-100 flex items-center justify-center mr-3 mt-1">
                                            <span className="w-2 h-2 bg-green-600 rounded-full"></span>
                                        </span>
                                        <span className="text-sm text-gray-700">
                                            Truy vấn và phân tích log dễ dàng với công cụ trực quan hóa Kibana
                                        </span>
                                    </li>
                                    <li className="flex items-start">
                                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-green-100 flex items-center justify-center mr-3 mt-1">
                                            <span className="w-2 h-2 bg-green-600 rounded-full"></span>
                                        </span>
                                        <span className="text-sm text-gray-700">
                                            Hỗ trợ giám sát lưu lượng, phân tích hành vi người dùng và tối ưu hệ thống
                                        </span>
                                    </li>
                                    <li className="flex items-start">
                                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-green-100 flex items-center justify-center mr-3 mt-1">
                                            <span className="w-2 h-2 bg-green-600 rounded-full"></span>
                                        </span>
                                        <span className="text-sm text-gray-700">
                                            Hỗ trợ tính năng tính phí API dựa trên mức độ sử dụng
                                        </span>
                                    </li>
                                    <li className="flex items-start">
                                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-green-100 flex items-center justify-center mr-3 mt-1">
                                            <span className="w-2 h-2 bg-green-600 rounded-full"></span>
                                        </span>
                                        <span className="text-sm text-gray-700">
                                            Debug và truy vết lỗi hiệu quả, giảm thiểu thời gian xử lý sự cố
                                        </span>
                                    </li>
                                </ul>
                            </div>
                        </div>

                        <div className="border-t border-dashed border-gray-200 pt-4">
                            <p className="text-sm text-gray-700 italic">
                                <span className="font-semibold">Công nghệ sử dụng:</span> Spring AOP, Kafka,
                                Elasticsearch, Kibana, Spring Boot Filter
                            </p>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 overflow-hidden border border-gray-100">
                    <div className="bg-gradient-to-r from-blue-600 to-sky-500 py-4 px-6">
                        <div className="flex items-center">
                            <span className="flex items-center justify-center w-10 h-10 rounded-full bg-white text-blue-600 font-bold text-lg mr-3">
                                4
                            </span>
                            <h3 className="text-xl font-bold text-white">
                                Quản lý Đăng nhập, Đăng xuất với Redis, JWT và OAuth 2.0
                            </h3>
                        </div>
                    </div>
                    <div className="p-6">
                        <div className="mb-4">
                            <h4 className="font-semibold text-gray-800 mb-2">Vấn đề gặp phải:</h4>
                            <p className="text-gray-700 mb-3">
                                Trong phát triển hệ thống SmartForms, việc quản lý đăng nhập, đăng xuất cần giải quyết
                                nhiều thách thức:
                            </p>
                            <ul className="space-y-2 mb-3">
                                <li className="flex items-start">
                                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center mr-3 mt-1">
                                        <span className="w-2 h-2 bg-blue-600 rounded-full"></span>
                                    </span>
                                    <span className="text-sm text-gray-700">Bảo mật thông tin xác thực người dùng</span>
                                </li>
                                <li className="flex items-start">
                                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center mr-3 mt-1">
                                        <span className="w-2 h-2 bg-blue-600 rounded-full"></span>
                                    </span>
                                    <span className="text-sm text-gray-700">
                                        Quản lý phiên đăng nhập hiệu quả và an toàn
                                    </span>
                                </li>
                                <li className="flex items-start">
                                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center mr-3 mt-1">
                                        <span className="w-2 h-2 bg-blue-600 rounded-full"></span>
                                    </span>
                                    <span className="text-sm text-gray-700">
                                        Giảm thiểu thiệt hại khi bị tấn công (như Man-in-the-Middle)
                                    </span>
                                </li>
                                <li className="flex items-start">
                                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center mr-3 mt-1">
                                        <span className="w-2 h-2 bg-blue-600 rounded-full"></span>
                                    </span>
                                    <span className="text-sm text-gray-700">
                                        Tối ưu hiệu năng khi kiểm tra trạng thái đăng nhập
                                    </span>
                                </li>
                                <li className="flex items-start">
                                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center mr-3 mt-1">
                                        <span className="w-2 h-2 bg-blue-600 rounded-full"></span>
                                    </span>
                                    <span className="text-sm text-gray-700">
                                        Xử lý vấn đề refresh token vẫn có thể dùng sau khi access token bị logout
                                    </span>
                                </li>
                            </ul>
                        </div>

                        <div className="mb-4">
                            <h4 className="font-semibold text-gray-800 mb-2">Giải pháp:</h4>
                            <div className="mb-3">
                                <p className="text-gray-700 mb-3">
                                    Triển khai hệ thống xác thực kết hợp JWT, Redis và OAuth 2.0:
                                </p>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                    <div className="p-3 bg-blue-50 rounded-lg">
                                        <h4 className="font-semibold text-blue-800 mb-1">JWT (JSON Web Token)</h4>
                                        <p className="text-sm text-gray-700">
                                            Sử dụng JWT để xác thực người dùng, chứa thông tin người dùng và được ký
                                            bằng thuật toán HMAC với secret key.
                                        </p>
                                    </div>
                                    <div className="p-3 bg-blue-50 rounded-lg">
                                        <h4 className="font-semibold text-blue-800 mb-1">
                                            Access Token & Refresh Token
                                        </h4>
                                        <p className="text-sm text-gray-700">
                                            Access Token (thời gian sống ngắn: 1 ngày) cho API, Refresh Token (7 ngày)
                                            để lấy Access Token mới.
                                        </p>
                                    </div>
                                    <div className="p-3 bg-blue-50 rounded-lg">
                                        <h4 className="font-semibold text-blue-800 mb-1">Redis</h4>
                                        <p className="text-sm text-gray-700">
                                            Lưu trữ danh sách token đã logout (blacklist) với TTL tương ứng thời gian
                                            hết hạn của token.
                                        </p>
                                    </div>
                                    <div className="p-3 bg-blue-50 rounded-lg">
                                        <h4 className="font-semibold text-blue-800 mb-1">OAuth 2.0</h4>
                                        <p className="text-sm text-gray-700">
                                            Hỗ trợ đăng nhập thông qua Google, tạo mật khẩu ngẫu nhiên và gửi qua email
                                            cho người dùng mới.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="rounded-lg overflow-hidden border border-gray-200 mb-4">
                                <div className="bg-gray-50 p-3 border-b border-gray-200">
                                    <h5 className="font-medium text-gray-700">Quản lý token đặc biệt</h5>
                                </div>
                                <div className="p-3">
                                    <ul className="space-y-2 list-inside text-sm text-gray-700">
                                        <li>
                                            <span className="font-semibold">jti (JWT ID):</span> Mỗi token có ID độc
                                            nhất (UUID)
                                        </li>
                                        <li>
                                            <span className="font-semibold">type:</span> Xác định loại token (ACCESS
                                            hoặc REFRESH)
                                        </li>
                                        <li>
                                            <span className="font-semibold">accessTokenId:</span> Refresh Token lưu jti
                                            của Access Token tương ứng
                                        </li>
                                        <li>
                                            <span className="font-semibold">Kiểm tra logout:</span> Khi refresh, kiểm
                                            tra xem accessTokenId có trong danh sách đen không
                                        </li>
                                    </ul>
                                </div>
                            </div>

                            <div className="rounded-lg overflow-hidden border border-gray-200 mb-4">
                                <div className="bg-gray-50 p-3 border-b border-gray-200">
                                    <h5 className="font-medium text-gray-700">Luồng xử lý đăng xuất</h5>
                                </div>
                                <div className="p-3">
                                    <ol className="space-y-2 list-decimal list-inside text-sm text-gray-700">
                                        <li>Người dùng gửi yêu cầu đăng xuất với Access Token</li>
                                        <li>
                                            Hệ thống trích xuất jti từ token và thêm vào Redis với key{' '}
                                            {'logoutToken-{jti}'}
                                        </li>
                                        <li>Key được đặt TTL tương ứng thời gian hết hạn của Access Token</li>
                                        <li>
                                            Khi có yêu cầu API mới, hệ thống kiểm tra xem jti của token có trong Redis
                                            không
                                        </li>
                                        <li>Nếu jti tồn tại trong Redis, yêu cầu bị từ chối (token đã logout)</li>
                                    </ol>
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 gap-4 mb-4">
                            <div className="p-3 bg-blue-50 rounded-lg">
                                <h4 className="font-semibold text-blue-800 mb-1">Kết quả đạt được:</h4>
                                <ul className="space-y-2">
                                    <li className="flex items-start">
                                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center mr-3 mt-1">
                                            <span className="w-2 h-2 bg-blue-600 rounded-full"></span>
                                        </span>
                                        <span className="text-sm text-gray-700">
                                            Bảo mật thông tin xác thực cao nhờ mã hóa HMAC và JWT
                                        </span>
                                    </li>
                                    <li className="flex items-start">
                                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center mr-3 mt-1">
                                            <span className="w-2 h-2 bg-blue-600 rounded-full"></span>
                                        </span>
                                        <span className="text-sm text-gray-700">
                                            Giảm thiểu thiệt hại khi bị tấn công MITM nhờ thời gian sống ngắn của Access
                                            Token
                                        </span>
                                    </li>
                                    <li className="flex items-start">
                                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center mr-3 mt-1">
                                            <span className="w-2 h-2 bg-blue-600 rounded-full"></span>
                                        </span>
                                        <span className="text-sm text-gray-700">
                                            Hiệu năng cao khi kiểm tra trạng thái đăng nhập nhờ Redis
                                        </span>
                                    </li>
                                    <li className="flex items-start">
                                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center mr-3 mt-1">
                                            <span className="w-2 h-2 bg-blue-600 rounded-full"></span>
                                        </span>
                                        <span className="text-sm text-gray-700">
                                            Đăng nhập nhanh chóng, tiện lợi qua OAuth 2.0 với Google
                                        </span>
                                    </li>
                                    <li className="flex items-start">
                                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center mr-3 mt-1">
                                            <span className="w-2 h-2 bg-blue-600 rounded-full"></span>
                                        </span>
                                        <span className="text-sm text-gray-700">
                                            Xử lý hiệu quả vấn đề refresh token sau khi access token đã logout
                                        </span>
                                    </li>
                                    <li className="flex items-start">
                                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center mr-3 mt-1">
                                            <span className="w-2 h-2 bg-blue-600 rounded-full"></span>
                                        </span>
                                        <span className="text-sm text-gray-700">
                                            Ngăn chặn việc dùng refresh token thay cho access token nhờ phân loại token
                                        </span>
                                    </li>
                                </ul>
                            </div>
                        </div>

                        <div className="border-t border-dashed border-gray-200 pt-4">
                            <p className="text-sm text-gray-700 italic">
                                <span className="font-semibold">Công nghệ sử dụng:</span> JWT, Redis, OAuth 2.0, Spring
                                Security, HMAC
                            </p>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 overflow-hidden border border-gray-100">
                    <div className="bg-gradient-to-r from-red-600 to-orange-500 py-4 px-6">
                        <div className="flex items-center">
                            <span className="flex items-center justify-center w-10 h-10 rounded-full bg-white text-red-600 font-bold text-lg mr-3">
                                5
                            </span>
                            <h3 className="text-xl font-bold text-white">
                                Giải pháp sử dụng Kafka cho các tác vụ ghi dữ liệu
                            </h3>
                        </div>
                    </div>
                    <div className="p-6">
                        <div className="mb-4">
                            <h4 className="font-semibold text-gray-800 mb-2">Vấn đề gặp phải:</h4>
                            <p className="text-gray-700 mb-3">
                                Trong vận hành SmartForms, đặc biệt với các tác vụ ghi dữ liệu như gửi đáp án, phản hồi
                                form và đặt hàng, đã phát sinh các vấn đề:
                            </p>
                            <ul className="space-y-2 mb-3">
                                <li className="flex items-start">
                                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-red-100 flex items-center justify-center mr-3 mt-1">
                                        <span className="w-2 h-2 bg-red-600 rounded-full"></span>
                                    </span>
                                    <span className="text-sm text-gray-700">
                                        Database trở thành nút thắt cổ chai khi số lượng request ghi dữ liệu lớn và đồng
                                        thời
                                    </span>
                                </li>
                                <li className="flex items-start">
                                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-red-100 flex items-center justify-center mr-3 mt-1">
                                        <span className="w-2 h-2 bg-red-600 rounded-full"></span>
                                    </span>
                                    <span className="text-sm text-gray-700">
                                        Lỗi gửi form khi nhiều người dùng thực hiện cùng lúc (như thời điểm nộp bài thi
                                        gần hết giờ)
                                    </span>
                                </li>
                                <li className="flex items-start">
                                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-red-100 flex items-center justify-center mr-3 mt-1">
                                        <span className="w-2 h-2 bg-red-600 rounded-full"></span>
                                    </span>
                                    <span className="text-sm text-gray-700">
                                        Tính nhất quán của dữ liệu không đảm bảo khi có nhiều thao tác ghi đồng thời
                                    </span>
                                </li>
                                <li className="flex items-start">
                                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-red-100 flex items-center justify-center mr-3 mt-1">
                                        <span className="w-2 h-2 bg-red-600 rounded-full"></span>
                                    </span>
                                    <span className="text-sm text-gray-700">
                                        Khó khăn trong việc mở rộng database và đồng bộ dữ liệu real-time khi scale out
                                    </span>
                                </li>
                            </ul>
                        </div>

                        <div className="mb-4">
                            <h4 className="font-semibold text-gray-800 mb-2">Giải pháp:</h4>
                            <p className="text-gray-700 mb-3">
                                Triển khai Kafka làm message queue cho các tác vụ ghi dữ liệu, thay đổi luồng xử lý từ
                                <span className="font-semibold"> Frontend → Backend → Database </span>
                                thành
                                <span className="font-semibold"> Frontend → Backend → Kafka → Backend → Database</span>:
                            </p>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                <div className="p-3 bg-red-50 rounded-lg">
                                    <h4 className="font-semibold text-red-800 mb-1">Kafka làm Message Queue</h4>
                                    <p className="text-sm text-gray-700">
                                        Thay vì ghi trực tiếp vào database, backend gửi dữ liệu dạng message đến các
                                        Kafka topic tương ứng (submit-answer, submit-form, place-order).
                                    </p>
                                </div>
                                <div className="p-3 bg-red-50 rounded-lg">
                                    <h4 className="font-semibold text-red-800 mb-1">Consumer xử lý Message</h4>
                                    <p className="text-sm text-gray-700">
                                        Các backend service lắng nghe Kafka topic, xử lý message bất đồng bộ, thực hiện
                                        logic nghiệp vụ và ghi dữ liệu vào database.
                                    </p>
                                </div>
                                <div className="p-3 bg-red-50 rounded-lg">
                                    <h4 className="font-semibold text-red-800 mb-1">Gửi từng đáp án</h4>
                                    <p className="text-sm text-gray-700">
                                        Form đề thi cho phép gửi từng đáp án ngay sau khi hoàn thành mỗi câu hỏi, giúp
                                        phân tải và giảm thiểu nguy cơ mất dữ liệu.
                                    </p>
                                </div>
                                <div className="p-3 bg-red-50 rounded-lg">
                                    <h4 className="font-semibold text-red-800 mb-1">Lý do chọn Kafka</h4>
                                    <p className="text-sm text-gray-700">
                                        Khả năng xử lý hàng triệu message/giây, tính bền vững qua lưu trữ trên disk,
                                        replication, phục hồi tự động, mở rộng dễ dàng và phù hợp microservice.
                                    </p>
                                </div>
                            </div>

                            <div className="rounded-lg overflow-hidden border border-gray-200 mb-4">
                                <div className="bg-gray-50 p-3 border-b border-gray-200">
                                    <h5 className="font-medium text-gray-700">Luồng xử lý dữ liệu với Kafka</h5>
                                </div>
                                <div className="p-3">
                                    <ol className="space-y-2 list-decimal list-inside text-sm text-gray-700">
                                        <li>
                                            Người dùng thực hiện tác vụ cần ghi dữ liệu (gửi đáp án, gửi form, đặt hàng)
                                        </li>
                                        <li>
                                            Backend service nhận request và gửi dữ liệu dưới dạng message đến Kafka
                                            topic tương ứng
                                        </li>
                                        <li>Kafka lưu trữ message trong topic, đảm bảo tính bền vững</li>
                                        <li>Consumer service lắng nghe topic, xử lý message bất đồng bộ</li>
                                        <li>Consumer thực hiện logic nghiệp vụ và ghi dữ liệu vào database</li>
                                        <li>
                                            Backend trả về response cho người dùng ngay sau khi gửi message đến Kafka
                                        </li>
                                    </ol>
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 gap-4 mb-4">
                            <div className="p-3 bg-red-50 rounded-lg">
                                <h4 className="font-semibold text-red-800 mb-1">Kết quả đạt được:</h4>
                                <ul className="space-y-2">
                                    <li className="flex items-start">
                                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-red-100 flex items-center justify-center mr-3 mt-1">
                                            <span className="w-2 h-2 bg-red-600 rounded-full"></span>
                                        </span>
                                        <span className="text-sm text-gray-700">
                                            Giảm tải cho database, tránh quá tải khi có nhiều request ghi dữ liệu đồng
                                            thời
                                        </span>
                                    </li>
                                    <li className="flex items-start">
                                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-red-100 flex items-center justify-center mr-3 mt-1">
                                            <span className="w-2 h-2 bg-red-600 rounded-full"></span>
                                        </span>
                                        <span className="text-sm text-gray-700">
                                            Tăng khả năng chịu lỗi với dữ liệu được lưu trữ an toàn và có thể phục hồi
                                        </span>
                                    </li>
                                    <li className="flex items-start">
                                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-red-100 flex items-center justify-center mr-3 mt-1">
                                            <span className="w-2 h-2 bg-red-600 rounded-full"></span>
                                        </span>
                                        <span className="text-sm text-gray-700">
                                            Đảm bảo tính nhất quán của dữ liệu qua xử lý message theo thứ tự
                                        </span>
                                    </li>
                                    <li className="flex items-start">
                                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-red-100 flex items-center justify-center mr-3 mt-1">
                                            <span className="w-2 h-2 bg-red-600 rounded-full"></span>
                                        </span>
                                        <span className="text-sm text-gray-700">
                                            Cải thiện trải nghiệm người dùng với hệ thống ổn định và mượt mà hơn
                                        </span>
                                    </li>
                                    <li className="flex items-start">
                                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-red-100 flex items-center justify-center mr-3 mt-1">
                                            <span className="w-2 h-2 bg-red-600 rounded-full"></span>
                                        </span>
                                        <span className="text-sm text-gray-700">
                                            Dễ dàng mở rộng hệ thống theo chiều ngang (scale out) bằng cách thêm
                                            consumer
                                        </span>
                                    </li>
                                    <li className="flex items-start">
                                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-red-100 flex items-center justify-center mr-3 mt-1">
                                            <span className="w-2 h-2 bg-red-600 rounded-full"></span>
                                        </span>
                                        <span className="text-sm text-gray-700">
                                            Giảm thiểu lỗi gửi form với khả năng gửi từng đáp án riêng biệt
                                        </span>
                                    </li>
                                </ul>
                            </div>
                        </div>

                        <div className="border-t border-dashed border-gray-200 pt-4">
                            <p className="text-sm text-gray-700 italic">
                                <span className="font-semibold">Công nghệ sử dụng:</span> Apache Kafka, Spring Boot,
                                Kafka Streams, Spring Kafka
                            </p>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 overflow-hidden border border-gray-100">
                    <div className="bg-gradient-to-r from-purple-700 to-pink-600 py-4 px-6">
                        <div className="flex items-center">
                            <span className="flex items-center justify-center w-10 h-10 rounded-full bg-white text-purple-700 font-bold text-lg mr-3">
                                6
                            </span>
                            <h3 className="text-xl font-bold text-white">
                                Giải pháp Xử lý Khuôn mặt với DeepFace và Chống Giả mạo
                            </h3>
                        </div>
                    </div>
                    <div className="p-6">
                        <div className="mb-4">
                            <h4 className="font-semibold text-gray-800 mb-2">Vấn đề gặp phải:</h4>
                            <p className="text-gray-700 mb-3">
                                Trong hệ thống SmartForms, việc tích hợp nhận diện khuôn mặt để xác thực người dùng (cho
                                điểm danh, thi cử, xác minh danh tính) đối mặt với những thách thức:
                            </p>
                            <ul className="space-y-2 mb-3">
                                <li className="flex items-start">
                                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-purple-100 flex items-center justify-center mr-3 mt-1">
                                        <span className="w-2 h-2 bg-purple-700 rounded-full"></span>
                                    </span>
                                    <span className="text-sm text-gray-700">
                                        Đảm bảo độ chính xác cao ngay cả với ảnh chất lượng thấp hoặc ánh sáng không tốt
                                    </span>
                                </li>
                                <li className="flex items-start">
                                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-purple-100 flex items-center justify-center mr-3 mt-1">
                                        <span className="w-2 h-2 bg-purple-700 rounded-full"></span>
                                    </span>
                                    <span className="text-sm text-gray-700">
                                        Phát hiện và ngăn chặn các hình thức giả mạo (ảnh chụp lại, video, mặt nạ)
                                    </span>
                                </li>
                                <li className="flex items-start">
                                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-purple-100 flex items-center justify-center mr-3 mt-1">
                                        <span className="w-2 h-2 bg-purple-700 rounded-full"></span>
                                    </span>
                                    <span className="text-sm text-gray-700">
                                        Tích hợp liền mạch với hệ thống hiện có (MinIO, MySQL)
                                    </span>
                                </li>
                                <li className="flex items-start">
                                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-purple-100 flex items-center justify-center mr-3 mt-1">
                                        <span className="w-2 h-2 bg-purple-700 rounded-full"></span>
                                    </span>
                                    <span className="text-sm text-gray-700">
                                        Khả năng mở rộng để xử lý nhiều yêu cầu đồng thời
                                    </span>
                                </li>
                            </ul>
                        </div>

                        <div className="mb-4">
                            <h4 className="font-semibold text-gray-800 mb-2">Giải pháp:</h4>
                            <p className="text-gray-700 mb-3">
                                Triển khai hệ thống nhận diện khuôn mặt dựa trên DeepFace kết hợp với cơ chế chống giả
                                mạo:
                            </p>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                <div className="p-3 bg-purple-50 rounded-lg">
                                    <h4 className="font-semibold text-purple-800 mb-1">
                                        DeepFace cho nhận diện khuôn mặt
                                    </h4>
                                    <ul className="space-y-1 text-sm text-gray-700">
                                        <li>• Tính toán embedding vector từ ảnh khuôn mặt</li>
                                        <li>• So sánh vector bằng khoảng cách Euclidean</li>
                                        <li>• Lưu ảnh trên MinIO, vector trong MySQL</li>
                                    </ul>
                                </div>
                                <div className="p-3 bg-purple-50 rounded-lg">
                                    <h4 className="font-semibold text-purple-800 mb-1">Cơ chế chống giả mạo</h4>
                                    <ul className="space-y-1 text-sm text-gray-700">
                                        <li>• Phân tích tần số cao bằng biến đổi Fourier</li>
                                        <li>• Kiểm tra độ sắc nét bằng phương pháp Laplacian</li>
                                        <li>• Kết hợp vào quy trình nhận diện khuôn mặt</li>
                                    </ul>
                                </div>
                            </div>

                            <div className="rounded-lg overflow-hidden border border-gray-200 mb-4">
                                <div className="bg-gray-50 p-3 border-b border-gray-200">
                                    <h5 className="font-medium text-gray-700">Quy trình xử lý khuôn mặt</h5>
                                </div>
                                <div className="p-3">
                                    <ol className="space-y-2 list-decimal list-inside text-sm text-gray-700">
                                        <li>Nhận hình ảnh khuôn mặt từ người dùng</li>
                                        <li>Kiểm tra chống giả mạo (phân tích tần số cao và độ sắc nét)</li>
                                        <li>Nếu vượt qua kiểm tra, tính toán embedding vector bằng DeepFace</li>
                                        <li>So sánh vector mới với vector đã lưu trong cơ sở dữ liệu</li>
                                        <li>Xác định kết quả nhận diện dựa trên khoảng cách Euclidean</li>
                                        <li>Lưu trữ kết quả và cập nhật cơ sở dữ liệu nếu cần</li>
                                    </ol>
                                </div>
                            </div>

                            <div className="rounded-lg overflow-hidden border border-gray-200 mb-4">
                                <div className="bg-gray-50 p-3 border-b border-gray-200">
                                    <h5 className="font-medium text-gray-700">Chi tiết phương pháp chống giả mạo</h5>
                                </div>
                                <div className="p-3 text-sm text-gray-700">
                                    <p className="mb-2">
                                        <span className="font-semibold">1. Phân tích tần số cao:</span> Sử dụng biến đổi
                                        Fourier để phân tích tần số trong ảnh. Ảnh giả mạo (chụp lại hoặc video) thường
                                        có tần số cao thấp hơn ảnh thật. Hệ thống tính tỷ lệ năng lượng ở tần số cao so
                                        với tổng năng lượng của ảnh.
                                    </p>
                                    <p>
                                        <span className="font-semibold">2. Kiểm tra độ sắc nét:</span> Sử dụng phương
                                        pháp Laplacian để đánh giá độ sắc nét. Ảnh giả mạo thường có độ sắc nét thấp hơn
                                        ảnh thật. Hệ thống tính toán phương sai của Laplacian để xác định độ sắc nét của
                                        ảnh.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 gap-4 mb-4">
                            <div className="p-3 bg-purple-50 rounded-lg">
                                <h4 className="font-semibold text-purple-800 mb-1">Kết quả đạt được:</h4>
                                <ul className="space-y-2">
                                    <li className="flex items-start">
                                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-purple-100 flex items-center justify-center mr-3 mt-1">
                                            <span className="w-2 h-2 bg-purple-700 rounded-full"></span>
                                        </span>
                                        <span className="text-sm text-gray-700">
                                            Độ chính xác cao trong nhận diện khuôn mặt với model Facenet512 của DeepFace
                                        </span>
                                    </li>
                                    <li className="flex items-start">
                                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-purple-100 flex items-center justify-center mr-3 mt-1">
                                            <span className="w-2 h-2 bg-purple-700 rounded-full"></span>
                                        </span>
                                        <span className="text-sm text-gray-700">
                                            Phát hiện và ngăn chặn hiệu quả các hình thức giả mạo phổ biến
                                        </span>
                                    </li>
                                    <li className="flex items-start">
                                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-purple-100 flex items-center justify-center mr-3 mt-1">
                                            <span className="w-2 h-2 bg-purple-700 rounded-full"></span>
                                        </span>
                                        <span className="text-sm text-gray-700">
                                            Tích hợp liền mạch với MinIO và MySQL cho quản lý dữ liệu hiệu quả
                                        </span>
                                    </li>
                                    <li className="flex items-start">
                                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-purple-100 flex items-center justify-center mr-3 mt-1">
                                            <span className="w-2 h-2 bg-purple-700 rounded-full"></span>
                                        </span>
                                        <span className="text-sm text-gray-700">
                                            Hiệu suất cao với khả năng xử lý nhiều yêu cầu mỗi giây nhờ xử lý đa luồng
                                        </span>
                                    </li>
                                    <li className="flex items-start">
                                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-purple-100 flex items-center justify-center mr-3 mt-1">
                                            <span className="w-2 h-2 bg-purple-700 rounded-full"></span>
                                        </span>
                                        <span className="text-sm text-gray-700">
                                            Khả năng mở rộng dễ dàng để đáp ứng yêu cầu thi cử hoặc điểm danh hàng loạt
                                        </span>
                                    </li>
                                    <li className="flex items-start">
                                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-purple-100 flex items-center justify-center mr-3 mt-1">
                                            <span className="w-2 h-2 bg-purple-700 rounded-full"></span>
                                        </span>
                                        <span className="text-sm text-gray-700">
                                            Phục hồi sau lỗi tốt nhờ cơ chế lưu trữ dữ liệu đảm bảo tính nhất quán
                                        </span>
                                    </li>
                                </ul>
                            </div>
                        </div>

                        <div className="border-t border-dashed border-gray-200 pt-4">
                            <p className="text-sm text-gray-700 italic">
                                <span className="font-semibold">Công nghệ sử dụng:</span> DeepFace, Python, MinIO,
                                MySQL, Biến đổi Fourier, Phương pháp Laplacian, Đa luồng
                            </p>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 overflow-hidden border border-gray-100">
                    <div className="bg-gradient-to-r from-cyan-600 to-emerald-600 py-4 px-6">
                        <div className="flex items-center">
                            <span className="flex items-center justify-center w-10 h-10 rounded-full bg-white text-cyan-600 font-bold text-lg mr-3">
                                7
                            </span>
                            <h3 className="text-xl font-bold text-white">
                                Giải pháp Mã hóa và Xác thực dữ liệu nhạy cảm
                            </h3>
                        </div>
                    </div>
                    <div className="p-6">
                        <div className="mb-4">
                            <h4 className="font-semibold text-gray-800 mb-2">Vấn đề gặp phải:</h4>
                            <p className="text-gray-700 mb-3">
                                Trong hệ thống SmartForms, các dữ liệu nhạy cảm như điểm số, nội dung phản hồi bài thi
                                cần được bảo vệ khỏi các truy cập trái phép và thay đổi bất hợp pháp:
                            </p>
                            <ul className="space-y-2 mb-3">
                                <li className="flex items-start">
                                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-cyan-100 flex items-center justify-center mr-3 mt-1">
                                        <span className="w-2 h-2 bg-cyan-600 rounded-full"></span>
                                    </span>
                                    <span className="text-sm text-gray-700">
                                        Bảo mật dữ liệu nhạy cảm, đảm bảo chỉ người có quyền mới đọc được
                                    </span>
                                </li>
                                <li className="flex items-start">
                                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-cyan-100 flex items-center justify-center mr-3 mt-1">
                                        <span className="w-2 h-2 bg-cyan-600 rounded-full"></span>
                                    </span>
                                    <span className="text-sm text-gray-700">
                                        Xác thực tính toàn vẹn của dữ liệu, phát hiện bất kỳ thay đổi nào
                                    </span>
                                </li>
                                <li className="flex items-start">
                                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-cyan-100 flex items-center justify-center mr-3 mt-1">
                                        <span className="w-2 h-2 bg-cyan-600 rounded-full"></span>
                                    </span>
                                    <span className="text-sm text-gray-700">
                                        Đảm bảo hiệu suất và khả năng mở rộng cho thi cử hàng loạt
                                    </span>
                                </li>
                            </ul>
                        </div>

                        <div className="mb-4">
                            <h4 className="font-semibold text-gray-800 mb-2">Giải pháp:</h4>
                            <p className="text-gray-700 mb-3">
                                Triển khai giải pháp kết hợp AES để mã hóa dữ liệu nhạy cảm và HMAC để xác thực tính
                                toàn vẹn:
                            </p>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                <div className="p-3 bg-cyan-50 rounded-lg">
                                    <h4 className="font-semibold text-cyan-800 mb-1">Mã hóa dữ liệu bằng AES</h4>
                                    <ul className="space-y-1 text-sm text-gray-700">
                                        <li>• Sử dụng thuật toán AES với chế độ CBC và padding PKCS5</li>
                                        <li>• Tạo khóa mã hóa từ chuỗi bí mật băm qua SHA-256</li>
                                        <li>• Sử dụng vector khởi tạo (IV) ngẫu nhiên cho mỗi lần mã hóa</li>
                                        <li>• Lưu IV cùng với dữ liệu mã hóa để sử dụng khi giải mã</li>
                                    </ul>
                                </div>
                                <div className="p-3 bg-cyan-50 rounded-lg">
                                    <h4 className="font-semibold text-cyan-800 mb-1">
                                        Xác thực tính toàn vẹn bằng HMAC
                                    </h4>
                                    <ul className="space-y-1 text-sm text-gray-700">
                                        <li>• Sử dụng thuật toán HMAC với hàm băm SHA-256</li>
                                        <li>• Tạo khóa HMAC từ chuỗi bí mật cấu hình trong hệ thống</li>
                                        <li>• Tạo checksum bằng cách băm dữ liệu cần xác thực</li>
                                        <li>• Lưu trữ checksum cùng với dữ liệu để kiểm tra sau này</li>
                                    </ul>
                                </div>
                            </div>

                            <div className="rounded-lg overflow-hidden border border-gray-200 mb-4">
                                <div className="bg-gray-50 p-3 border-b border-gray-200">
                                    <h5 className="font-medium text-gray-700">Quy trình mã hóa và xác thực</h5>
                                </div>
                                <div className="p-3">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <h6 className="font-semibold text-sm text-gray-700 mb-2">
                                                Quá trình mã hóa:
                                            </h6>
                                            <ol className="space-y-1 list-decimal list-inside text-sm text-gray-700">
                                                <li>Tạo IV ngẫu nhiên 16 byte</li>
                                                <li>Tạo khóa mã hóa từ chuỗi bí mật qua SHA-256</li>
                                                <li>Thực hiện mã hóa dữ liệu với AES/CBC/PKCS5Padding</li>
                                                <li>Ghép IV vào đầu dữ liệu đã mã hóa</li>
                                                <li>Tạo checksum HMAC cho dữ liệu đã mã hóa (bao gồm IV)</li>
                                                <li>Lưu trữ cả dữ liệu đã mã hóa và checksum</li>
                                            </ol>
                                        </div>
                                        <div>
                                            <h6 className="font-semibold text-sm text-gray-700 mb-2">
                                                Quá trình giải mã và xác thực:
                                            </h6>
                                            <ol className="space-y-1 list-decimal list-inside text-sm text-gray-700">
                                                <li>Tính lại checksum HMAC cho dữ liệu đã mã hóa</li>
                                                <li>So sánh với checksum đã lưu trữ, nếu khác nhau thì từ chối</li>
                                                <li>Tách IV từ 16 byte đầu của dữ liệu đã mã hóa</li>
                                                <li>Tạo lại khóa mã hóa từ chuỗi bí mật</li>
                                                <li>Thực hiện giải mã dữ liệu với AES/CBC/PKCS5Padding</li>
                                                <li>Trả về dữ liệu gốc đã giải mã</li>
                                            </ol>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 gap-4 mb-4">
                            <div className="p-3 bg-cyan-50 rounded-lg">
                                <h4 className="font-semibold text-cyan-800 mb-1">Kết quả đạt được:</h4>
                                <ul className="space-y-2">
                                    <li className="flex items-start">
                                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-cyan-100 flex items-center justify-center mr-3 mt-1">
                                            <span className="w-2 h-2 bg-cyan-600 rounded-full"></span>
                                        </span>
                                        <span className="text-sm text-gray-700">
                                            Tăng cường bảo mật dữ liệu, chỉ người có quyền mới đọc được thông tin nhạy
                                            cảm
                                        </span>
                                    </li>
                                    <li className="flex items-start">
                                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-cyan-100 flex items-center justify-center mr-3 mt-1">
                                            <span className="w-2 h-2 bg-cyan-600 rounded-full"></span>
                                        </span>
                                        <span className="text-sm text-gray-700">
                                            Đảm bảo tính toàn vẹn dữ liệu, phát hiện mọi thay đổi trái phép
                                        </span>
                                    </li>
                                    <li className="flex items-start">
                                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-cyan-100 flex items-center justify-center mr-3 mt-1">
                                            <span className="w-2 h-2 bg-cyan-600 rounded-full"></span>
                                        </span>
                                        <span className="text-sm text-gray-700">
                                            Hiệu suất hệ thống được tối ưu nhờ thuật toán xử lý nhanh
                                        </span>
                                    </li>
                                    <li className="flex items-start">
                                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-cyan-100 flex items-center justify-center mr-3 mt-1">
                                            <span className="w-2 h-2 bg-cyan-600 rounded-full"></span>
                                        </span>
                                        <span className="text-sm text-gray-700">
                                            Khả năng mở rộng linh hoạt cho các tình huống thi cử hàng loạt
                                        </span>
                                    </li>
                                    <li className="flex items-start">
                                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-cyan-100 flex items-center justify-center mr-3 mt-1">
                                            <span className="w-2 h-2 bg-cyan-600 rounded-full"></span>
                                        </span>
                                        <span className="text-sm text-gray-700">
                                            Nâng cao độ tin cậy của hệ thống trong môi trường giáo dục
                                        </span>
                                    </li>
                                    <li className="flex items-start">
                                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-cyan-100 flex items-center justify-center mr-3 mt-1">
                                            <span className="w-2 h-2 bg-cyan-600 rounded-full"></span>
                                        </span>
                                        <span className="text-sm text-gray-700">
                                            Giảm thiểu rủi ro rò rỉ thông tin và tuân thủ tốt hơn yêu cầu về bảo mật
                                        </span>
                                    </li>
                                </ul>
                            </div>
                        </div>

                        <div className="border-t border-dashed border-gray-200 pt-4">
                            <p className="text-sm text-gray-700 italic">
                                <span className="font-semibold">Công nghệ sử dụng:</span> AES (CBC mode), HMAC, SHA-256,
                                Java Cryptography Extension (JCE), PKCS5Padding
                            </p>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 overflow-hidden border border-gray-100">
                    <div className="bg-gradient-to-r from-amber-600 to-yellow-500 py-4 px-6">
                        <div className="flex items-center">
                            <span className="flex items-center justify-center w-10 h-10 rounded-full bg-white text-amber-600 font-bold text-lg mr-3">
                                8
                            </span>
                            <h3 className="text-xl font-bold text-white">Giải pháp Phân tích Đa tiêu chí bằng AHP</h3>
                        </div>
                    </div>
                    <div className="p-6">
                        <div className="mb-4">
                            <h4 className="font-semibold text-gray-800 mb-2">Vấn đề gặp phải:</h4>
                            <p className="text-gray-700 mb-3">
                                Trong hệ thống SmartForms, đánh giá và lựa chọn các phương án dựa trên nhiều tiêu chí
                                khác nhau (như xếp hạng bài thi, đánh giá lựa chọn trong form) gặp khó khăn khi:
                            </p>
                            <ul className="space-y-2 mb-3">
                                <li className="flex items-start">
                                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-amber-100 flex items-center justify-center mr-3 mt-1">
                                        <span className="w-2 h-2 bg-amber-600 rounded-full"></span>
                                    </span>
                                    <span className="text-sm text-gray-700">
                                        Cần một phương pháp khoa học để xác định trọng số cho từng tiêu chí
                                    </span>
                                </li>
                                <li className="flex items-start">
                                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-amber-100 flex items-center justify-center mr-3 mt-1">
                                        <span className="w-2 h-2 bg-amber-600 rounded-full"></span>
                                    </span>
                                    <span className="text-sm text-gray-700">
                                        Việc xác định trọng số thủ công dẫn đến thiếu nhất quán và không khách quan
                                    </span>
                                </li>
                                <li className="flex items-start">
                                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-amber-100 flex items-center justify-center mr-3 mt-1">
                                        <span className="w-2 h-2 bg-amber-600 rounded-full"></span>
                                    </span>
                                    <span className="text-sm text-gray-700">
                                        Cần tính toán điểm tổng hợp chính xác dựa trên nhiều tiêu chí
                                    </span>
                                </li>
                            </ul>
                        </div>

                        <div className="mb-4">
                            <h4 className="font-semibold text-gray-800 mb-2">Giải pháp:</h4>
                            <p className="text-gray-700 mb-3">
                                Áp dụng phương pháp Phân tích Thứ bậc (Analytic Hierarchy Process - AHP) để ra quyết
                                định đa tiêu chí một cách khoa học, với các bước:
                            </p>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                <div className="p-3 bg-amber-50 rounded-lg">
                                    <h4 className="font-semibold text-amber-800 mb-1">Xây dựng ma trận so sánh cặp</h4>
                                    <ul className="space-y-1 text-sm text-gray-700">
                                        <li>• So sánh các tiêu chí theo cặp, thang điểm 1-9</li>
                                        <li>
                                            • Ma trận A với phần tử a<sub>ij</sub> biểu thị mức độ quan trọng của tiêu
                                            chí i so với j
                                        </li>
                                        <li>• Đường chéo chính có giá trị 1 (mỗi tiêu chí bằng chính nó)</li>
                                        <li>
                                            • Các phần tử đối xứng có quan hệ a<sub>ji</sub> = 1/a<sub>ij</sub>
                                        </li>
                                    </ul>
                                </div>
                                <div className="p-3 bg-amber-50 rounded-lg">
                                    <h4 className="font-semibold text-amber-800 mb-1">
                                        Tính vector trọng số (eigenvector)
                                    </h4>
                                    <ul className="space-y-1 text-sm text-gray-700">
                                        <li>• Tính geometric mean cho mỗi hàng: lấy tích và căn bậc n</li>
                                        <li>• Chuẩn hóa vector: chia mỗi giá trị cho tổng tất cả giá trị</li>
                                        <li>• Kết quả là vector trọng số của các tiêu chí</li>
                                        <li>• Tổng các trọng số luôn bằng 1</li>
                                    </ul>
                                </div>
                                <div className="p-3 bg-amber-50 rounded-lg">
                                    <h4 className="font-semibold text-amber-800 mb-1">Kiểm tra tính nhất quán</h4>
                                    <ul className="space-y-1 text-sm text-gray-700">
                                        <li>
                                            • Tính λ<sub>max</sub> (lambda max): giá trị riêng lớn nhất của ma trận
                                        </li>
                                        <li>
                                            • Tính Consistency Index (CI): CI = (λ<sub>max</sub> - n)/(n - 1)
                                        </li>
                                        <li>• Tính Consistency Ratio (CR): CR = CI/RI</li>
                                        <li>• Nếu CR &lt; 0.1, ma trận được coi là nhất quán</li>
                                    </ul>
                                </div>
                                <div className="p-3 bg-amber-50 rounded-lg">
                                    <h4 className="font-semibold text-amber-800 mb-1">Tính điểm tổng hợp</h4>
                                    <ul className="space-y-1 text-sm text-gray-700">
                                        <li>• Với mỗi lựa chọn, nhân trọng số từng tiêu chí với điểm tương ứng</li>
                                        <li>• Cộng dồn các giá trị đã nhân để ra điểm tổng hợp</li>
                                        <li>• Xếp hạng các lựa chọn dựa trên điểm tổng hợp</li>
                                        <li>• Lựa chọn có điểm cao nhất là tối ưu nhất</li>
                                    </ul>
                                </div>
                            </div>

                            <div className="rounded-lg overflow-hidden border border-gray-200 mb-4">
                                <div className="bg-gray-50 p-3 border-b border-gray-200">
                                    <h5 className="font-medium text-gray-700">Công thức tính toán trong AHP</h5>
                                </div>
                                <div className="p-3 text-sm text-gray-700">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <p className="font-semibold mb-1">Geometric Mean của hàng thứ i:</p>
                                            <p className="italic">
                                                GM<sub>i</sub> = (∏<sub>j=1</sub>
                                                <sup>n</sup> a<sub>ij</sub>)<sup>1/n</sup>
                                            </p>
                                            <p className="mt-2 font-semibold mb-1">Vector trọng số (chuẩn hóa):</p>
                                            <p className="italic">
                                                w<sub>i</sub> = GM<sub>i</sub> / ∑<sub>j=1</sub>
                                                <sup>n</sup> GM<sub>j</sub>
                                            </p>
                                        </div>
                                        <div>
                                            <p className="font-semibold mb-1">Consistency Index (CI):</p>
                                            <p className="italic">
                                                CI = (λ<sub>max</sub> - n) / (n - 1)
                                            </p>
                                            <p className="mt-2 font-semibold mb-1">Consistency Ratio (CR):</p>
                                            <p className="italic">CR = CI / RI</p>
                                            <p className="text-xs mt-1">
                                                Với RI là Random Index, giá trị tham chiếu dựa trên kích thước ma trận
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 gap-4 mb-4">
                            <div className="p-3 bg-amber-50 rounded-lg">
                                <h4 className="font-semibold text-amber-800 mb-1">Kết quả đạt được:</h4>
                                <ul className="space-y-2">
                                    <li className="flex items-start">
                                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-amber-100 flex items-center justify-center mr-3 mt-1">
                                            <span className="w-2 h-2 bg-amber-600 rounded-full"></span>
                                        </span>
                                        <span className="text-sm text-gray-700">
                                            Xác định trọng số khoa học và khách quan cho các tiêu chí
                                        </span>
                                    </li>
                                    <li className="flex items-start">
                                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-amber-100 flex items-center justify-center mr-3 mt-1">
                                            <span className="w-2 h-2 bg-amber-600 rounded-full"></span>
                                        </span>
                                        <span className="text-sm text-gray-700">
                                            Kiểm soát được tính nhất quán trong đánh giá của người dùng (CR &lt; 0.1)
                                        </span>
                                    </li>
                                    <li className="flex items-start">
                                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-amber-100 flex items-center justify-center mr-3 mt-1">
                                            <span className="w-2 h-2 bg-amber-600 rounded-full"></span>
                                        </span>
                                        <span className="text-sm text-gray-700">
                                            Tính toán điểm tổng hợp chính xác, minh bạch cho các lựa chọn
                                        </span>
                                    </li>
                                    <li className="flex items-start">
                                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-amber-100 flex items-center justify-center mr-3 mt-1">
                                            <span className="w-2 h-2 bg-amber-600 rounded-full"></span>
                                        </span>
                                        <span className="text-sm text-gray-700">
                                            Cải thiện trải nghiệm người dùng trong đánh giá và lựa chọn đa tiêu chí
                                        </span>
                                    </li>
                                    <li className="flex items-start">
                                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-amber-100 flex items-center justify-center mr-3 mt-1">
                                            <span className="w-2 h-2 bg-amber-600 rounded-full"></span>
                                        </span>
                                        <span className="text-sm text-gray-700">
                                            Khả năng mở rộng áp dụng cho nhiều bài toán đa tiêu chí khác nhau trong hệ
                                            thống
                                        </span>
                                    </li>
                                </ul>
                            </div>
                        </div>

                        <div className="border-t border-dashed border-gray-200 pt-4">
                            <p className="text-sm text-gray-700 italic">
                                <span className="font-semibold">Ứng dụng thực tế:</span> Đánh giá bài thi, xếp hạng học
                                sinh, lựa chọn phương án trong form phân cấp thứ bậc, hỗ trợ ra quyết định đa tiêu chí
                            </p>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 overflow-hidden border border-gray-100">
                    <div className="bg-gradient-to-r from-red-500 to-pink-500 py-4 px-6">
                        <div className="flex items-center">
                            <span className="flex items-center justify-center w-10 h-10 rounded-full bg-white text-red-500 font-bold text-lg mr-3">
                                9
                            </span>
                            <h3 className="text-xl font-bold text-white">Giải pháp Lưu trữ Tạm thời với Redis</h3>
                        </div>
                    </div>
                    <div className="p-6">
                        <div className="mb-4">
                            <h4 className="font-semibold text-gray-800 mb-2">Vấn đề gặp phải:</h4>
                            <p className="text-gray-700 mb-3">
                                Trong vận hành hệ thống SmartForms, đặc biệt với tính năng gửi phản hồi form, xuất hiện
                                các vấn đề ảnh hưởng đến hiệu năng và trải nghiệm người dùng:
                            </p>
                            <ul className="space-y-2 mb-3">
                                <li className="flex items-start">
                                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-red-100 flex items-center justify-center mr-3 mt-1">
                                        <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                                    </span>
                                    <span className="text-sm text-gray-700">
                                        Tải cao trên Database khi nhiều người dùng đồng thời gửi phản hồi form
                                    </span>
                                </li>
                                <li className="flex items-start">
                                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-red-100 flex items-center justify-center mr-3 mt-1">
                                        <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                                    </span>
                                    <span className="text-sm text-gray-700">
                                        Độ trễ khi xử lý phản hồi do thời gian ghi vào database kéo dài
                                    </span>
                                </li>
                                <li className="flex items-start">
                                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-red-100 flex items-center justify-center mr-3 mt-1">
                                        <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                                    </span>
                                    <span className="text-sm text-gray-700">
                                        Lưu trữ không cần thiết vào Database khi người dùng điền form nhưng chưa nộp
                                    </span>
                                </li>
                                <li className="flex items-start">
                                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-red-100 flex items-center justify-center mr-3 mt-1">
                                        <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                                    </span>
                                    <span className="text-sm text-gray-700">
                                        Mất dữ liệu tạm thời khi tải lại trang hoặc gặp sự cố kết nối
                                    </span>
                                </li>
                                <li className="flex items-start">
                                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-red-100 flex items-center justify-center mr-3 mt-1">
                                        <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                                    </span>
                                    <span className="text-sm text-gray-700">
                                        Khó khăn trong việc mở rộng database khi cần xử lý lượng lớn request
                                    </span>
                                </li>
                            </ul>
                        </div>

                        <div className="mb-4">
                            <h4 className="font-semibold text-gray-800 mb-2">Giải pháp:</h4>
                            <p className="text-gray-700 mb-3">
                                Triển khai Redis làm bộ lưu trữ tạm thời cho các phản hồi form, tận dụng ưu điểm lưu trữ
                                dữ liệu trong bộ nhớ (in-memory) với hiệu suất cao:
                            </p>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                <div className="p-3 bg-red-50 rounded-lg">
                                    <h4 className="font-semibold text-red-800 mb-1">Lưu trữ tạm thời trong Redis</h4>
                                    <ul className="space-y-1 text-sm text-gray-700">
                                        <li>• Lưu phản hồi form với cấu trúc key-value</li>
                                        <li>• Key là định danh duy nhất của phiên làm việc</li>
                                        <li>• Value là nội dung phản hồi dạng JSON</li>
                                        <li>• Thiết lập TTL (Time To Live) cho dữ liệu</li>
                                    </ul>
                                </div>
                                <div className="p-3 bg-red-50 rounded-lg">
                                    <h4 className="font-semibold text-red-800 mb-1">Truy xuất dữ liệu nhanh chóng</h4>
                                    <ul className="space-y-1 text-sm text-gray-700">
                                        <li>• Khi tải lại trang, lấy dữ liệu từ Redis thay vì database</li>
                                        <li>• Tốc độ đọc/ghi nhanh (microseconds)</li>
                                        <li>• Giảm tải cho database khi nhiều người dùng</li>
                                        <li>• Dữ liệu hiển thị ngay lập tức cho người dùng</li>
                                    </ul>
                                </div>
                                <div className="p-3 bg-red-50 rounded-lg">
                                    <h4 className="font-semibold text-red-800 mb-1">Lưu trữ chính thức khi nộp form</h4>
                                    <ul className="space-y-1 text-sm text-gray-700">
                                        <li>• Chỉ chuyển dữ liệu từ Redis sang database khi nộp</li>
                                        <li>• Tránh lưu trữ không cần thiết vào database</li>
                                        <li>• Giảm tải và tối ưu hóa tài nguyên</li>
                                        <li>• Khi nộp thành công, xóa dữ liệu tạm thời trên Redis</li>
                                    </ul>
                                </div>
                                <div className="p-3 bg-red-50 rounded-lg">
                                    <h4 className="font-semibold text-red-800 mb-1">
                                        Đảm bảo tính nhất quán và mở rộng
                                    </h4>
                                    <ul className="space-y-1 text-sm text-gray-700">
                                        <li>• Sử dụng Redis persistence đảm bảo dữ liệu bền vững</li>
                                        <li>• Ghi log các phản hồi đã xử lý để tránh mất dữ liệu</li>
                                        <li>• Redis Cluster cho khả năng mở rộng theo chiều ngang</li>
                                        <li>• Tách biệt lưu trữ tạm thời và xử lý dữ liệu chính thức</li>
                                    </ul>
                                </div>
                            </div>

                            <div className="rounded-lg overflow-hidden border border-gray-200 mb-4">
                                <div className="bg-gray-50 p-3 border-b border-gray-200">
                                    <h5 className="font-medium text-gray-700">Luồng xử lý phản hồi form với Redis</h5>
                                </div>
                                <div className="p-3">
                                    <ol className="space-y-2 list-decimal list-inside text-sm text-gray-700">
                                        <li>Người dùng điền thông tin vào form</li>
                                        <li>Dữ liệu được gửi tới server và lưu tạm thời vào Redis với TTL định sẵn</li>
                                        <li>
                                            Khi người dùng tải lại trang, dữ liệu được truy xuất từ Redis và hiển thị
                                            lại
                                        </li>
                                        <li>Người dùng tiếp tục điền thông tin, hệ thống định kỳ cập nhật vào Redis</li>
                                        <li>Khi hoàn thành và nộp form, dữ liệu được chuyển từ Redis sang database</li>
                                        <li>
                                            Sau khi lưu vào database thành công, dữ liệu tạm thời trên Redis được xóa
                                        </li>
                                    </ol>
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 gap-4 mb-4">
                            <div className="p-3 bg-red-50 rounded-lg">
                                <h4 className="font-semibold text-red-800 mb-1">Kết quả đạt được:</h4>
                                <ul className="space-y-2">
                                    <li className="flex items-start">
                                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-red-100 flex items-center justify-center mr-3 mt-1">
                                            <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                                        </span>
                                        <span className="text-sm text-gray-700">
                                            Giảm tải đáng kể cho database, tránh tình trạng quá tải khi nhiều người dùng
                                            đồng thời
                                        </span>
                                    </li>
                                    <li className="flex items-start">
                                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-red-100 flex items-center justify-center mr-3 mt-1">
                                            <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                                        </span>
                                        <span className="text-sm text-gray-700">
                                            Cải thiện trải nghiệm người dùng, không mất dữ liệu khi tải lại trang hoặc
                                            gặp sự cố
                                        </span>
                                    </li>
                                    <li className="flex items-start">
                                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-red-100 flex items-center justify-center mr-3 mt-1">
                                            <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                                        </span>
                                        <span className="text-sm text-gray-700">
                                            Đảm bảo tính nhất quán và độ tin cậy của dữ liệu với Redis persistence và cơ
                                            chế ghi log
                                        </span>
                                    </li>
                                    <li className="flex items-start">
                                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-red-100 flex items-center justify-center mr-3 mt-1">
                                            <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                                        </span>
                                        <span className="text-sm text-gray-700">
                                            Khả năng mở rộng dễ dàng với Redis Cluster, đáp ứng nhu cầu xử lý lượng lớn
                                            request
                                        </span>
                                    </li>
                                    <li className="flex items-start">
                                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-red-100 flex items-center justify-center mr-3 mt-1">
                                            <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                                        </span>
                                        <span className="text-sm text-gray-700">
                                            Tối ưu hóa tài nguyên, chỉ lưu trữ dữ liệu chính thức vào database khi cần
                                            thiết
                                        </span>
                                    </li>
                                </ul>
                            </div>
                        </div>

                        <div className="border-t border-dashed border-gray-200 pt-4">
                            <p className="text-sm text-gray-700 italic">
                                <span className="font-semibold">Công nghệ sử dụng:</span> Redis, Redis Cluster, Redis
                                Persistence, Spring Data Redis, JSON, Time-To-Live (TTL)
                            </p>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 overflow-hidden border border-gray-100">
                    <div className="bg-gradient-to-r from-indigo-600 to-violet-600 py-4 px-6">
                        <div className="flex items-center">
                            <span className="flex items-center justify-center w-10 h-10 rounded-full bg-white text-indigo-600 font-bold text-lg mr-3">
                                10
                            </span>
                            <h3 className="text-xl font-bold text-white">
                                Giải pháp phản hồi real-time với WebSocket và JWT
                            </h3>
                        </div>
                    </div>
                    <div className="p-6">
                        <div className="mb-4">
                            <h4 className="font-semibold text-gray-800 mb-2">Vấn đề gặp phải:</h4>
                            <p className="text-gray-700 mb-3">
                                Trong hệ thống SmartForms, khi người dùng gửi phản hồi, cần thông báo real-time tới chủ
                                sở hữu form và người có quyền chỉnh sửa, đòi hỏi giải quyết các thách thức:
                            </p>
                            <ul className="space-y-2 mb-3">
                                <li className="flex items-start">
                                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-indigo-100 flex items-center justify-center mr-3 mt-1">
                                        <span className="w-2 h-2 bg-indigo-600 rounded-full"></span>
                                    </span>
                                    <span className="text-sm text-gray-700">
                                        Xác thực người dùng đảm bảo chỉ người đăng nhập và có quyền mới nhận thông báo
                                    </span>
                                </li>
                                <li className="flex items-start">
                                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-indigo-100 flex items-center justify-center mr-3 mt-1">
                                        <span className="w-2 h-2 bg-indigo-600 rounded-full"></span>
                                    </span>
                                    <span className="text-sm text-gray-700">
                                        Giao tiếp real-time để gửi thông báo ngay lập tức mà không cần refresh trang
                                    </span>
                                </li>
                                <li className="flex items-start">
                                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-indigo-100 flex items-center justify-center mr-3 mt-1">
                                        <span className="w-2 h-2 bg-indigo-600 rounded-full"></span>
                                    </span>
                                    <span className="text-sm text-gray-700">
                                        Phân quyền chính xác chỉ gửi thông báo tới người có quyền chỉnh sửa
                                    </span>
                                </li>
                                <li className="flex items-start">
                                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-indigo-100 flex items-center justify-center mr-3 mt-1">
                                        <span className="w-2 h-2 bg-indigo-600 rounded-full"></span>
                                    </span>
                                    <span className="text-sm text-gray-700">
                                        Hiệu suất cao, không ảnh hưởng tới luồng xử lý chính của hệ thống
                                    </span>
                                </li>
                            </ul>
                        </div>

                        <div className="mb-4">
                            <h4 className="font-semibold text-gray-800 mb-2">Giải pháp:</h4>
                            <p className="text-gray-700 mb-3">
                                Kết hợp WebSocket và JWT để xác thực người dùng và gửi thông báo real-time một cách an
                                toàn và hiệu quả:
                            </p>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                <div className="p-3 bg-indigo-50 rounded-lg">
                                    <h4 className="font-semibold text-indigo-800 mb-1">Xác thực người dùng bằng JWT</h4>
                                    <ul className="space-y-1 text-sm text-gray-700">
                                        <li>• Yêu cầu JWT thông qua query parameter khi kết nối WebSocket</li>
                                        <li>• Xác thực JWT bằng hàm verifyToken tự triển khai</li>
                                        <li>• Kiểm tra tính hợp lệ và trích xuất thông tin người dùng</li>
                                        <li>• Lưu email người dùng trong ConcurrentHashMap khi token hợp lệ</li>
                                    </ul>
                                </div>
                                <div className="p-3 bg-indigo-50 rounded-lg">
                                    <h4 className="font-semibold text-indigo-800 mb-1">Quản lý kết nối WebSocket</h4>
                                    <ul className="space-y-1 text-sm text-gray-700">
                                        <li>• Lưu session ID và email người dùng trong ConcurrentHashMap</li>
                                        <li>• Theo dõi người dùng đang kết nối để gửi thông báo khi cần</li>
                                        <li>• Xóa thông tin khỏi ConcurrentHashMap khi người dùng ngắt kết nối</li>
                                        <li>• Đảm bảo thread-safe với ConcurrentHashMap</li>
                                    </ul>
                                </div>
                                <div className="p-3 bg-indigo-50 rounded-lg">
                                    <h4 className="font-semibold text-indigo-800 mb-1">Gửi thông báo real-time</h4>
                                    <ul className="space-y-1 text-sm text-gray-700">
                                        <li>• Khi có phản hồi, xác định chủ sở hữu và người có quyền EDIT</li>
                                        <li>• Kiểm tra người dùng nào đang kết nối WebSocket</li>
                                        <li>• Gửi thông báo qua WebSocket tới những người dùng phù hợp</li>
                                        <li>• Sử dụng session ID đã lưu trong ConcurrentHashMap</li>
                                    </ul>
                                </div>
                                <div className="p-3 bg-indigo-50 rounded-lg">
                                    <h4 className="font-semibold text-indigo-800 mb-1">Xử lý bất đồng bộ</h4>
                                    <ul className="space-y-1 text-sm text-gray-700">
                                        <li>• Sử dụng ExecutorService để xử lý gửi thông báo bất đồng bộ</li>
                                        <li>• Tránh block luồng xử lý chính khi thực hiện tác vụ I/O</li>
                                        <li>• Tối ưu hiệu suất của hệ thống tổng thể</li>
                                        <li>• Đảm bảo khả năng xử lý đồng thời nhiều thông báo</li>
                                    </ul>
                                </div>
                            </div>

                            <div className="rounded-lg overflow-hidden border border-gray-200 mb-4">
                                <div className="bg-gray-50 p-3 border-b border-gray-200">
                                    <h5 className="font-medium text-gray-700">Luồng xử lý thông báo real-time</h5>
                                </div>
                                <div className="p-3">
                                    <ol className="space-y-2 list-decimal list-inside text-sm text-gray-700">
                                        <li>Người dùng kết nối tới WebSocket, cung cấp JWT qua query parameter</li>
                                        <li>Backend xác thực JWT, trích xuất thông tin người dùng (email)</li>
                                        <li>Nếu hợp lệ, lưu session ID và email người dùng vào ConcurrentHashMap</li>
                                        <li>
                                            Khi có người dùng gửi phản hồi form, hệ thống xác định chủ sở hữu và người
                                            có quyền EDIT
                                        </li>
                                        <li>
                                            Kiểm tra những người này đang kết nối WebSocket dựa trên email trong
                                            ConcurrentHashMap
                                        </li>
                                        <li>
                                            Sử dụng ExecutorService để gửi thông báo bất đồng bộ tới các session ID
                                            tương ứng
                                        </li>
                                        <li>Người dùng nhận thông báo real-time mà không cần refresh trang</li>
                                    </ol>
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 gap-4 mb-4">
                            <div className="p-3 bg-indigo-50 rounded-lg">
                                <h4 className="font-semibold text-indigo-800 mb-1">Kết quả đạt được:</h4>
                                <ul className="space-y-2">
                                    <li className="flex items-start">
                                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-indigo-100 flex items-center justify-center mr-3 mt-1">
                                            <span className="w-2 h-2 bg-indigo-600 rounded-full"></span>
                                        </span>
                                        <span className="text-sm text-gray-700">
                                            Xác thực người dùng an toàn nhờ JWT, đảm bảo chỉ người đăng nhập mới nhận
                                            thông báo
                                        </span>
                                    </li>
                                    <li className="flex items-start">
                                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-indigo-100 flex items-center justify-center mr-3 mt-1">
                                            <span className="w-2 h-2 bg-indigo-600 rounded-full"></span>
                                        </span>
                                        <span className="text-sm text-gray-700">
                                            Giao tiếp real-time hiệu quả qua WebSocket, thông báo ngay lập tức không cần
                                            refresh
                                        </span>
                                    </li>
                                    <li className="flex items-start">
                                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-indigo-100 flex items-center justify-center mr-3 mt-1">
                                            <span className="w-2 h-2 bg-indigo-600 rounded-full"></span>
                                        </span>
                                        <span className="text-sm text-gray-700">
                                            Phân quyền chính xác, chỉ gửi thông báo tới chủ sở hữu và người có quyền
                                            EDIT
                                        </span>
                                    </li>
                                    <li className="flex items-start">
                                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-indigo-100 flex items-center justify-center mr-3 mt-1">
                                            <span className="w-2 h-2 bg-indigo-600 rounded-full"></span>
                                        </span>
                                        <span className="text-sm text-gray-700">
                                            Hiệu suất cao với xử lý bất đồng bộ qua ExecutorService, không ảnh hưởng
                                            luồng chính
                                        </span>
                                    </li>
                                    <li className="flex items-start">
                                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-indigo-100 flex items-center justify-center mr-3 mt-1">
                                            <span className="w-2 h-2 bg-indigo-600 rounded-full"></span>
                                        </span>
                                        <span className="text-sm text-gray-700">
                                            Cải thiện trải nghiệm người dùng với cập nhật thông tin tức thì, không gián
                                            đoạn
                                        </span>
                                    </li>
                                </ul>
                            </div>
                        </div>

                        <div className="border-t border-dashed border-gray-200 pt-4">
                            <p className="text-sm text-gray-700 italic">
                                <span className="font-semibold">Công nghệ sử dụng:</span> WebSocket, JWT,
                                ConcurrentHashMap, ExecutorService, Spring, Async Processing
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </>
    ),
    features: () => (
        <>
            <h2 className="text-2xl font-bold mb-6">Tính năng chính</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100">
                    <div className="bg-gradient-to-r from-blue-500 to-indigo-600 px-6 py-4 flex items-center">
                        <div className="w-10 h-10 flex-shrink-0 bg-white rounded-full flex items-center justify-center mr-4">
                            <AppstoreOutlined className="text-blue-600 text-xl" />
                        </div>
                        <h3 className="text-xl font-bold text-white">Form đa dạng</h3>
                    </div>

                    <div className="p-6">
                        <ul className="space-y-4">
                            <li className="flex items-start">
                                <div className="flex-shrink-0 w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mr-3 mt-1">
                                    <span className="text-blue-600 text-xs font-bold">✓</span>
                                </div>
                                <div>
                                    <p className="text-gray-700">
                                        <span className="font-semibold">Nhiều loại form chuyên biệt</span> bao gồm thu
                                        thập dữ liệu, đề thi, bán hàng, điểm danh, nhận diện và AHP
                                    </p>
                                </div>
                            </li>
                            <li className="flex items-start">
                                <div className="flex-shrink-0 w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mr-3 mt-1">
                                    <span className="text-blue-600 text-xs font-bold">✓</span>
                                </div>
                                <div>
                                    <p className="text-gray-700">
                                        <span className="font-semibold">Ngân hàng câu hỏi</span> được phân loại theo
                                        khóa học và chương học
                                    </p>
                                </div>
                            </li>
                            <li className="flex items-start">
                                <div className="flex-shrink-0 w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mr-3 mt-1">
                                    <span className="text-blue-600 text-xs font-bold">✓</span>
                                </div>
                                <div>
                                    <p className="text-gray-700">
                                        <span className="font-semibold">Form AHP</span> cho phân tích quyết định đa tiêu
                                        chí chuyên sâu
                                    </p>
                                </div>
                            </li>
                        </ul>

                        <div className="mt-4">
                            <img
                                src="/assets/images/projects/smartforms/tao-form.png"
                                alt="Tạo form đa dạng"
                                className="rounded-lg shadow-sm"
                            />
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100">
                    <div className="bg-gradient-to-r from-green-500 to-teal-600 px-6 py-4 flex items-center">
                        <div className="w-10 h-10 flex-shrink-0 bg-white rounded-full flex items-center justify-center mr-4">
                            <SolutionOutlined className="text-green-600 text-xl" />
                        </div>
                        <h3 className="text-xl font-bold text-white">Hệ thống thi cử</h3>
                    </div>

                    <div className="p-6">
                        <ul className="space-y-4">
                            <li className="flex items-start">
                                <div className="flex-shrink-0 w-6 h-6 bg-green-100 rounded-full flex items-center justify-center mr-3 mt-1">
                                    <span className="text-green-600 text-xs font-bold">✓</span>
                                </div>
                                <div>
                                    <p className="text-gray-700">
                                        <span className="font-semibold">Tạo đề thi thông minh</span> với các tùy chọn đa
                                        dạng và linh hoạt
                                    </p>
                                </div>
                            </li>
                            <li className="flex items-start">
                                <div className="flex-shrink-0 w-6 h-6 bg-green-100 rounded-full flex items-center justify-center mr-3 mt-1">
                                    <span className="text-green-600 text-xs font-bold">✓</span>
                                </div>
                                <div>
                                    <p className="text-gray-700">
                                        <span className="font-semibold">Chấm điểm tự động</span> và mã hóa bảo mật bài
                                        làm
                                    </p>
                                </div>
                            </li>
                            <li className="flex items-start">
                                <div className="flex-shrink-0 w-6 h-6 bg-green-100 rounded-full flex items-center justify-center mr-3 mt-1">
                                    <span className="text-green-600 text-xs font-bold">✓</span>
                                </div>
                                <div>
                                    <p className="text-gray-700">
                                        <span className="font-semibold">Phân tích chi tiết kết quả</span> với các biểu
                                        đồ và thống kê
                                    </p>
                                </div>
                            </li>
                        </ul>

                        <div className="mt-4">
                            <img
                                src="/assets/images/projects/smartforms/tao-de-thi.png"
                                alt="Hệ thống thi cử"
                                className="rounded-lg shadow-sm"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100">
                    <div className="bg-gradient-to-r from-purple-500 to-pink-500 px-6 py-4 flex items-center">
                        <div className="w-10 h-10 flex-shrink-0 bg-white rounded-full flex items-center justify-center mr-4">
                            <DatabaseOutlined className="text-purple-600 text-xl" />
                        </div>
                        <h3 className="text-xl font-bold text-white">Phân tích dữ liệu</h3>
                    </div>

                    <div className="p-6">
                        <ul className="space-y-4">
                            <li className="flex items-start">
                                <div className="flex-shrink-0 w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center mr-3 mt-1">
                                    <span className="text-purple-600 text-xs font-bold">✓</span>
                                </div>
                                <div>
                                    <p className="text-gray-700">
                                        <span className="font-semibold">Thống kê chi tiết</span> các phản hồi và hoạt
                                        động người dùng
                                    </p>
                                </div>
                            </li>
                            <li className="flex items-start">
                                <div className="flex-shrink-0 w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center mr-3 mt-1">
                                    <span className="text-purple-600 text-xs font-bold">✓</span>
                                </div>
                                <div>
                                    <p className="text-gray-700">
                                        <span className="font-semibold">Báo cáo trực quan</span> với nhiều loại biểu đồ
                                        và bảng dữ liệu
                                    </p>
                                </div>
                            </li>
                            <li className="flex items-start">
                                <div className="flex-shrink-0 w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center mr-3 mt-1">
                                    <span className="text-purple-600 text-xs font-bold">✓</span>
                                </div>
                                <div>
                                    <p className="text-gray-700">
                                        <span className="font-semibold">Phân tích chuyên sâu</span> cho form AHP và đề
                                        thi
                                    </p>
                                </div>
                            </li>
                        </ul>

                        <div className="mt-4">
                            <img
                                src="/assets/images/projects/smartforms/thong-ke-cua-client-1.png"
                                alt="Phân tích dữ liệu"
                                className="rounded-lg shadow-sm"
                            />
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100">
                    <div className="bg-gradient-to-r from-amber-500 to-orange-600 px-6 py-4 flex items-center">
                        <div className="w-10 h-10 flex-shrink-0 bg-white rounded-full flex items-center justify-center mr-4">
                            <CodeOutlined className="text-amber-600 text-xl" />
                        </div>
                        <h3 className="text-xl font-bold text-white">Công nghệ tiên tiến</h3>
                    </div>

                    <div className="p-6">
                        <ul className="space-y-4">
                            <li className="flex items-start">
                                <div className="flex-shrink-0 w-6 h-6 bg-amber-100 rounded-full flex items-center justify-center mr-3 mt-1">
                                    <span className="text-amber-600 text-xs font-bold">✓</span>
                                </div>
                                <div>
                                    <p className="text-gray-700">
                                        <span className="font-semibold">Nhận diện khuôn mặt</span> cho điểm danh và định
                                        danh người dùng
                                    </p>
                                </div>
                            </li>
                            <li className="flex items-start">
                                <div className="flex-shrink-0 w-6 h-6 bg-amber-100 rounded-full flex items-center justify-center mr-3 mt-1">
                                    <span className="text-amber-600 text-xs font-bold">✓</span>
                                </div>
                                <div>
                                    <p className="text-gray-700">
                                        <span className="font-semibold">Cập nhật thời gian thực</span> với WebSocket và
                                        FCM
                                    </p>
                                </div>
                            </li>
                            <li className="flex items-start">
                                <div className="flex-shrink-0 w-6 h-6 bg-amber-100 rounded-full flex items-center justify-center mr-3 mt-1">
                                    <span className="text-amber-600 text-xs font-bold">✓</span>
                                </div>
                                <div>
                                    <p className="text-gray-700">
                                        <span className="font-semibold">Bảo mật cao cấp</span> với mã hóa AES, JWT và
                                        OAuth2
                                    </p>
                                </div>
                            </li>
                        </ul>

                        <div className="mt-4">
                            <img
                                src="/assets/images/projects/smartforms/quan-ly-nguoi-lam-bai-thi.png"
                                alt="Công nghệ tiên tiến"
                                className="rounded-lg shadow-sm"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100">
                    <div className="bg-gradient-to-r from-blue-600 to-cyan-600 px-6 py-4 flex items-center">
                        <div className="w-10 h-10 flex-shrink-0 bg-white rounded-full flex items-center justify-center mr-4">
                            <DashboardOutlined className="text-blue-600 text-xl" />
                        </div>
                        <h3 className="text-xl font-bold text-white">Quản lý tài nguyên</h3>
                    </div>

                    <div className="p-6">
                        <ul className="space-y-4">
                            <li className="flex items-start">
                                <div className="flex-shrink-0 w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mr-3 mt-1">
                                    <span className="text-blue-600 text-xs font-bold">✓</span>
                                </div>
                                <div>
                                    <p className="text-gray-700">
                                        <span className="font-semibold">Hệ thống thư mục phân cấp</span> cho việc tổ
                                        chức form hiệu quả
                                    </p>
                                </div>
                            </li>
                            <li className="flex items-start">
                                <div className="flex-shrink-0 w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mr-3 mt-1">
                                    <span className="text-blue-600 text-xs font-bold">✓</span>
                                </div>
                                <div>
                                    <p className="text-gray-700">
                                        <span className="font-semibold">Chia sẻ và phân quyền</span> chi tiết cho người
                                        dùng và nhóm
                                    </p>
                                </div>
                            </li>
                            <li className="flex items-start">
                                <div className="flex-shrink-0 w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mr-3 mt-1">
                                    <span className="text-blue-600 text-xs font-bold">✓</span>
                                </div>
                                <div>
                                    <p className="text-gray-700">
                                        <span className="font-semibold">Đánh dấu sao form quan trọng</span> và thùng rác
                                        để khôi phục
                                    </p>
                                </div>
                            </li>
                        </ul>

                        <div className="mt-4">
                            <img
                                src="/assets/images/projects/smartforms/danh-sach-thu-muc.png"
                                alt="Quản lý tài nguyên"
                                className="rounded-lg shadow-sm"
                            />
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100">
                    <div className="bg-gradient-to-r from-indigo-600 to-violet-600 px-6 py-4 flex items-center">
                        <div className="w-10 h-10 flex-shrink-0 bg-white rounded-full flex items-center justify-center mr-4">
                            <CloudOutlined className="text-indigo-600 text-xl" />
                        </div>
                        <h3 className="text-xl font-bold text-white">DevSecOps & Giám sát</h3>
                    </div>

                    <div className="p-6">
                        <ul className="space-y-4">
                            <li className="flex items-start">
                                <div className="flex-shrink-0 w-6 h-6 bg-indigo-100 rounded-full flex items-center justify-center mr-3 mt-1">
                                    <span className="text-indigo-600 text-xs font-bold">✓</span>
                                </div>
                                <div>
                                    <p className="text-gray-700">
                                        <span className="font-semibold">CI/CD tự động</span> với GitLab, Docker và
                                        Kubernetes
                                    </p>
                                </div>
                            </li>
                            <li className="flex items-start">
                                <div className="flex-shrink-0 w-6 h-6 bg-indigo-100 rounded-full flex items-center justify-center mr-3 mt-1">
                                    <span className="text-indigo-600 text-xs font-bold">✓</span>
                                </div>
                                <div>
                                    <p className="text-gray-700">
                                        <span className="font-semibold">Giám sát hiệu suất</span> với Zabbix và công cụ
                                        K6
                                    </p>
                                </div>
                            </li>
                            <li className="flex items-start">
                                <div className="flex-shrink-0 w-6 h-6 bg-indigo-100 rounded-full flex items-center justify-center mr-3 mt-1">
                                    <span className="text-indigo-600 text-xs font-bold">✓</span>
                                </div>
                                <div>
                                    <p className="text-gray-700">
                                        <span className="font-semibold">Quét bảo mật tự động</span> với Snyk và Trivy
                                    </p>
                                </div>
                            </li>
                        </ul>

                        <div className="mt-4">
                            <img
                                src="/assets/images/projects/smartforms/thong-ke-cua-admin-ve-he-thong-1.png"
                                alt="DevSecOps & Giám sát"
                                className="rounded-lg shadow-sm"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </>
    ),
    technologies: () => (
        <>
            <h2 className="text-2xl font-bold mb-6">Công nghệ sử dụng</h2>

            <div className="space-y-8">
                {/* Frontend */}
                <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100">
                    <div className="bg-gradient-to-r from-blue-500 to-indigo-600 px-6 py-4 flex items-center">
                        <div className="w-10 h-10 flex-shrink-0 bg-white rounded-full flex items-center justify-center mr-4">
                            <CodeOutlined className="text-blue-600 text-xl" />
                        </div>
                        <h3 className="text-xl font-bold text-white">Frontend</h3>
                    </div>

                    <div className="p-6">
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                            <div className="bg-blue-50 rounded-lg p-4 text-center hover:shadow-md transition-shadow duration-300 flex flex-col items-center justify-center">
                                <div className="w-12 h-12 flex items-center justify-center bg-white rounded-full shadow-sm mb-3">
                                    <img src="/assets/logos/react-2.svg" alt="ReactJS" className="w-6 h-6" />
                                </div>
                                <span className="font-medium text-gray-800">ReactJS</span>
                                <span className="text-xs text-gray-500 mt-1">Thư viện UI</span>
                            </div>

                            <div className="bg-blue-50 rounded-lg p-4 text-center hover:shadow-md transition-shadow duration-300 flex flex-col items-center justify-center">
                                <div className="w-12 h-12 flex items-center justify-center bg-white rounded-full shadow-sm mb-3">
                                    <img src="/assets/logos/redux.svg" alt="Redux Toolkit" className="w-6 h-6" />
                                </div>
                                <span className="font-medium text-gray-800">Redux Toolkit</span>
                                <span className="text-xs text-gray-500 mt-1">Quản lý state</span>
                            </div>

                            <div className="bg-blue-50 rounded-lg p-4 text-center hover:shadow-md transition-shadow duration-300 flex flex-col items-center justify-center">
                                <div className="w-12 h-12 flex items-center justify-center bg-white rounded-full shadow-sm mb-3">
                                    <img src="/assets/logos/antd.svg" alt="Ant Design" className="w-6 h-6" />
                                </div>
                                <span className="font-medium text-gray-800">Ant Design</span>
                                <span className="text-xs text-gray-500 mt-1">UI Component</span>
                            </div>

                            <div className="bg-blue-50 rounded-lg p-4 text-center hover:shadow-md transition-shadow duration-300 flex flex-col items-center justify-center">
                                <div className="w-12 h-12 flex items-center justify-center bg-white rounded-full shadow-sm mb-3">
                                    <img src="/assets/logos/sass.png" alt="SCSS" className="w-6 h-6" />
                                </div>
                                <span className="font-medium text-gray-800">SCSS</span>
                                <span className="text-xs text-gray-500 mt-1">CSS Preprocessor</span>
                            </div>

                            <div className="bg-blue-50 rounded-lg p-4 text-center hover:shadow-md transition-shadow duration-300 flex flex-col items-center justify-center">
                                <div className="w-12 h-12 flex items-center justify-center bg-white rounded-full shadow-sm mb-3">
                                    <img src="/assets/logos/javascript.png" alt="JavaScript" className="w-6 h-6" />
                                </div>
                                <span className="font-medium text-gray-800">JavaScript</span>
                                <span className="text-xs text-gray-500 mt-1">Ngôn ngữ</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Backend */}
                <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100">
                    <div className="bg-gradient-to-r from-green-500 to-teal-600 px-6 py-4 flex items-center">
                        <div className="w-10 h-10 flex-shrink-0 bg-white rounded-full flex items-center justify-center mr-4">
                            <CodeOutlined className="text-green-600 text-xl" />
                        </div>
                        <h3 className="text-xl font-bold text-white">Backend</h3>
                    </div>

                    <div className="p-6">
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                            <div className="bg-green-50 rounded-lg p-4 text-center hover:shadow-md transition-shadow duration-300 flex flex-col items-center justify-center">
                                <div className="w-12 h-12 flex items-center justify-center bg-white rounded-full shadow-sm mb-3">
                                    <img src="/assets/logos/spring-boot.png" alt="Spring Boot" className="w-8 h-8" />
                                </div>
                                <span className="font-medium text-gray-800">Spring Boot</span>
                                <span className="text-xs text-gray-500 mt-1">Framework</span>
                            </div>

                            <div className="bg-green-50 rounded-lg p-4 text-center hover:shadow-md transition-shadow duration-300 flex flex-col items-center justify-center">
                                <div className="w-12 h-12 flex items-center justify-center bg-white rounded-full shadow-sm mb-3">
                                    <img
                                        src="/assets/logos/spring-webflux.png"
                                        alt="Spring Webflux"
                                        className="w-8 h-8"
                                    />
                                </div>
                                <span className="font-medium text-gray-800">Spring Webflux</span>
                                <span className="text-xs text-gray-500 mt-1">Reactive API</span>
                            </div>

                            <div className="bg-green-50 rounded-lg p-4 text-center hover:shadow-md transition-shadow duration-300 flex flex-col items-center justify-center">
                                <div className="w-12 h-12 flex items-center justify-center bg-white rounded-full shadow-sm mb-3">
                                    <img
                                        src="/assets/logos/spring-security.png"
                                        alt="Spring Security"
                                        className="w-8 h-8"
                                    />
                                </div>
                                <span className="font-medium text-gray-800">Spring Security</span>
                                <span className="text-xs text-gray-500 mt-1">Bảo mật</span>
                            </div>

                            <div className="bg-green-50 rounded-lg p-4 text-center hover:shadow-md transition-shadow duration-300 flex flex-col items-center justify-center">
                                <div className="w-12 h-12 flex items-center justify-center bg-white rounded-full shadow-sm mb-3">
                                    <img src="/assets/logos/spring-boot.png" alt="Spring AOP" className="w-8 h-8" />
                                </div>
                                <span className="font-medium text-gray-800">Spring AOP</span>
                                <span className="text-xs text-gray-500 mt-1">Aspect</span>
                            </div>

                            <div className="bg-green-50 rounded-lg p-4 text-center hover:shadow-md transition-shadow duration-300 flex flex-col items-center justify-center">
                                <div className="w-12 h-12 flex items-center justify-center bg-white rounded-full shadow-sm mb-3">
                                    <img src="/assets/logos/spring-cloud.png" alt="Spring Cloud" className="w-8 h-8" />
                                </div>
                                <span className="font-medium text-gray-800">Spring Cloud</span>
                                <span className="text-xs text-gray-500 mt-1">Microservices</span>
                            </div>

                            <div className="bg-green-50 rounded-lg p-4 text-center hover:shadow-md transition-shadow duration-300 flex flex-col items-center justify-center">
                                <div className="w-12 h-12 flex items-center justify-center bg-white rounded-full shadow-sm mb-3">
                                    <img src="/assets/logos/python.png" alt="Python" className="w-6 h-6" />
                                </div>
                                <span className="font-medium text-gray-800">Python</span>
                                <span className="text-xs text-gray-500 mt-1">AI & CV</span>
                            </div>

                            <div className="bg-green-50 rounded-lg p-4 text-center hover:shadow-md transition-shadow duration-300 flex flex-col items-center justify-center">
                                <div className="w-12 h-12 flex items-center justify-center bg-white rounded-full shadow-sm mb-3">
                                    <img src="/assets/logos/flask.svg" alt="Flask" className="w-6 h-6" />
                                </div>
                                <span className="font-medium text-gray-800">Flask</span>
                                <span className="text-xs text-gray-500 mt-1">Microframework</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Database */}
                <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100">
                    <div className="bg-gradient-to-r from-yellow-500 to-amber-600 px-6 py-4 flex items-center">
                        <div className="w-10 h-10 flex-shrink-0 bg-white rounded-full flex items-center justify-center mr-4">
                            <DatabaseOutlined className="text-yellow-600 text-xl" />
                        </div>
                        <h3 className="text-xl font-bold text-white">Cơ sở dữ liệu</h3>
                    </div>

                    <div className="p-6">
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            <div className="bg-yellow-50 rounded-lg p-4 text-center hover:shadow-md transition-shadow duration-300 flex flex-col items-center justify-center">
                                <div className="w-12 h-12 flex items-center justify-center bg-white rounded-full shadow-sm mb-3">
                                    <img src="/assets/logos/mysql.png" alt="MySQL" className="w-8 h-8" />
                                </div>
                                <span className="font-medium text-gray-800">MySQL</span>
                                <span className="text-xs text-gray-500 mt-1">Relational Database</span>
                                <div className="mt-2 text-xs text-gray-600">
                                    <span className="inline-block bg-yellow-100 rounded-full px-2 py-1">
                                        Dữ liệu chính
                                    </span>
                                </div>
                            </div>

                            <div className="bg-yellow-50 rounded-lg p-4 text-center hover:shadow-md transition-shadow duration-300 flex flex-col items-center justify-center">
                                <div className="w-12 h-12 flex items-center justify-center bg-white rounded-full shadow-sm mb-3">
                                    <img src="/assets/logos/redis.svg" alt="Redis" className="w-8 h-8" />
                                </div>
                                <span className="font-medium text-gray-800">Redis</span>
                                <span className="text-xs text-gray-500 mt-1">In-memory Database</span>
                                <div className="mt-2 text-xs text-gray-600">
                                    <span className="inline-block bg-yellow-100 rounded-full px-2 py-1">Cache</span>
                                    <span className="inline-block bg-yellow-100 rounded-full px-2 py-1 ml-1">
                                        Session
                                    </span>
                                </div>
                            </div>

                            <div className="bg-yellow-50 rounded-lg p-4 text-center hover:shadow-md transition-shadow duration-300 flex flex-col items-center justify-center">
                                <div className="w-12 h-12 flex items-center justify-center bg-white rounded-full shadow-sm mb-3">
                                    <img
                                        src="/assets/logos/elasticsearch.svg"
                                        alt="Elasticsearch"
                                        className="w-8 h-8"
                                    />
                                </div>
                                <span className="font-medium text-gray-800">Elasticsearch</span>
                                <span className="text-xs text-gray-500 mt-1">Search Engine</span>
                                <div className="mt-2 text-xs text-gray-600">
                                    <span className="inline-block bg-yellow-100 rounded-full px-2 py-1">Log</span>
                                    <span className="inline-block bg-yellow-100 rounded-full px-2 py-1 ml-1">
                                        Search
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Cloud & DevOps */}
                <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100">
                    <div className="bg-gradient-to-r from-blue-600 to-indigo-700 px-6 py-4 flex items-center">
                        <div className="w-10 h-10 flex-shrink-0 bg-white rounded-full flex items-center justify-center mr-4">
                            <CloudOutlined className="text-blue-700 text-xl" />
                        </div>
                        <h3 className="text-xl font-bold text-white">Cloud & DevOps</h3>
                    </div>

                    <div className="p-6">
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                            <div className="bg-indigo-50 rounded-lg p-4 text-center hover:shadow-md transition-shadow duration-300 flex flex-col items-center justify-center">
                                <div className="w-12 h-12 flex items-center justify-center bg-white rounded-full shadow-sm mb-3">
                                    <img src="/assets/logos/docker.png" alt="Docker" className="w-8 h-8" />
                                </div>
                                <span className="font-medium text-gray-800">Docker</span>
                                <span className="text-xs text-gray-500 mt-1">Containerization</span>
                            </div>

                            <div className="bg-indigo-50 rounded-lg p-4 text-center hover:shadow-md transition-shadow duration-300 flex flex-col items-center justify-center">
                                <div className="w-12 h-12 flex items-center justify-center bg-white rounded-full shadow-sm mb-3">
                                    <img src="/assets/logos/kubernetes.png" alt="Kubernetes" className="w-8 h-8" />
                                </div>
                                <span className="font-medium text-gray-800">Kubernetes</span>
                                <span className="text-xs text-gray-500 mt-1">Orchestration</span>
                            </div>

                            <div className="bg-indigo-50 rounded-lg p-4 text-center hover:shadow-md transition-shadow duration-300 flex flex-col items-center justify-center">
                                <div className="w-12 h-12 flex items-center justify-center bg-white rounded-full shadow-sm mb-3">
                                    <img src="/assets/logos/gitlab.png" alt="GitLab CI/CD" className="w-8 h-8" />
                                </div>
                                <span className="font-medium text-gray-800">GitLab CI/CD</span>
                                <span className="text-xs text-gray-500 mt-1">Pipeline</span>
                            </div>

                            <div className="bg-indigo-50 rounded-lg p-4 text-center hover:shadow-md transition-shadow duration-300 flex flex-col items-center justify-center">
                                <div className="w-12 h-12 flex items-center justify-center bg-white rounded-full shadow-sm mb-3">
                                    <img src="/assets/logos/nginx.png" alt="Nginx" className="w-8 h-8" />
                                </div>
                                <span className="font-medium text-gray-800">Nginx</span>
                                <span className="text-xs text-gray-500 mt-1">Web Server</span>
                            </div>

                            <div className="bg-indigo-50 rounded-lg p-4 text-center hover:shadow-md transition-shadow duration-300 flex flex-col items-center justify-center">
                                <div className="w-12 h-12 flex items-center justify-center bg-white rounded-full shadow-sm mb-3">
                                    <img src="/assets/logos/portus.png" alt="Portus Registry" className="w-8 h-8" />
                                </div>
                                <span className="font-medium text-gray-800">Portus Registry</span>
                                <span className="text-xs text-gray-500 mt-1">Image Registry</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Công nghệ khác */}
                <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100">
                    <div className="bg-gradient-to-r from-purple-500 to-pink-600 px-6 py-4 flex items-center">
                        <div className="w-10 h-10 flex-shrink-0 bg-white rounded-full flex items-center justify-center mr-4">
                            <AppstoreOutlined className="text-purple-600 text-xl" />
                        </div>
                        <h3 className="text-xl font-bold text-white">Công nghệ chuyên dụng</h3>
                    </div>

                    <div className="p-6">
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                            <div className="bg-purple-50 rounded-lg p-3 text-center hover:shadow-md transition-shadow duration-300 flex flex-col items-center justify-center">
                                <div className="w-12 h-12 flex items-center justify-center bg-white rounded-full shadow-sm mb-3">
                                    <img src="/assets/logos/kafka.png" alt="Portus Registry" className="w-8 h-8" />
                                </div>
                                <span className="font-medium text-gray-800">Kafka</span>
                                <span className="text-xs text-gray-500 mt-1">Message Broker</span>
                            </div>

                            <div className="bg-purple-50 rounded-lg p-3 text-center hover:shadow-md transition-shadow duration-300 flex flex-col items-center justify-center">
                                <div className="w-12 h-12 flex items-center justify-center bg-white rounded-full shadow-sm mb-3">
                                    <img src="/assets/logos/websocket.png" alt="Portus Registry" className="w-8 h-8" />
                                </div>
                                <span className="font-medium text-gray-800">WebSocket</span>
                                <span className="text-xs text-gray-500 mt-1">Real-time Comm</span>
                            </div>

                            <div className="bg-purple-50 rounded-lg p-3 text-center hover:shadow-md transition-shadow duration-300 flex flex-col items-center justify-center">
                                <div className="w-12 h-12 flex items-center justify-center bg-white rounded-full shadow-sm mb-3">
                                    <img src="/assets/logos/fcm.svg" alt="Portus Registry" className="w-8 h-8" />
                                </div>
                                <span className="font-medium text-gray-800">FCM</span>
                                <span className="text-xs text-gray-500 mt-1">Push Notification</span>
                            </div>

                            <div className="bg-purple-50 rounded-lg p-3 text-center hover:shadow-md transition-shadow duration-300 flex flex-col items-center justify-center">
                                <div className="w-12 h-12 flex items-center justify-center bg-white rounded-full shadow-sm mb-3">
                                    <img src="/assets/logos/jwt.png" alt="Portus Registry" className="w-8 h-8" />
                                </div>
                                <span className="font-medium text-gray-800">JWT</span>
                                <span className="text-xs text-gray-500 mt-1">Token Auth</span>
                            </div>

                            <div className="bg-purple-50 rounded-lg p-3 text-center hover:shadow-md transition-shadow duration-300 flex flex-col items-center justify-center">
                                <div className="w-12 h-12 flex items-center justify-center bg-white rounded-full shadow-sm mb-3">
                                    <img src="/assets/logos/oauth2.png" alt="Portus Registry" className="w-8 h-8" />
                                </div>
                                <span className="font-medium text-gray-800">OAuth2</span>
                                <span className="text-xs text-gray-500 mt-1">Authorization</span>
                            </div>

                            <div className="bg-purple-50 rounded-lg p-3 text-center hover:shadow-md transition-shadow duration-300 flex flex-col items-center justify-center">
                                <div className="w-12 h-12 flex items-center justify-center bg-white rounded-full shadow-sm mb-3">
                                    <img src="/assets/logos/minio.png" alt="Portus Registry" className="w-8 h-8" />
                                </div>
                                <span className="font-medium text-gray-800">MinIO</span>
                                <span className="text-xs text-gray-500 mt-1">Object Storage</span>
                            </div>

                            <div className="bg-purple-50 rounded-lg p-3 text-center hover:shadow-md transition-shadow duration-300 flex flex-col items-center justify-center">
                                <div className="w-12 h-12 flex items-center justify-center bg-white rounded-full shadow-sm mb-3">
                                    <img src="/assets/logos/deepface.png" alt="Portus Registry" className="w-8 h-8" />
                                </div>
                                <span className="font-medium text-gray-800">Deepface</span>
                                <span className="text-xs text-gray-500 mt-1">Face Recognition</span>
                            </div>

                            <div className="bg-purple-50 rounded-lg p-3 text-center hover:shadow-md transition-shadow duration-300 flex flex-col items-center justify-center">
                                <div className="w-12 h-12 flex items-center justify-center bg-white rounded-full shadow-sm mb-3">
                                    <img src="/assets/logos/zabbix.png" alt="Portus Registry" className="w-8 h-8" />
                                </div>
                                <span className="font-medium text-gray-800">Zabbix</span>
                                <span className="text-xs text-gray-500 mt-1">Monitoring</span>
                            </div>

                            <div className="bg-purple-50 rounded-lg p-3 text-center hover:shadow-md transition-shadow duration-300 flex flex-col items-center justify-center">
                                <div className="w-12 h-12 flex items-center justify-center bg-white rounded-full shadow-sm mb-3">
                                    <img src="/assets/logos/k6.png" alt="Portus Registry" className="w-8 h-8" />
                                </div>
                                <span className="font-medium text-gray-800">K6</span>
                                <span className="text-xs text-gray-500 mt-1">Load Testing</span>
                            </div>

                            <div className="bg-purple-50 rounded-lg p-3 text-center hover:shadow-md transition-shadow duration-300 flex flex-col items-center justify-center">
                                <div className="w-12 h-12 flex items-center justify-center bg-white rounded-full shadow-sm mb-3">
                                    <img src="/assets/logos/snyk.png" alt="Portus Registry" className="w-8 h-8" />
                                </div>
                                <span className="font-medium text-gray-800">Snyk</span>
                                <span className="text-xs text-gray-500 mt-1">Security Scanning</span>
                            </div>

                            <div className="bg-purple-50 rounded-lg p-3 text-center hover:shadow-md transition-shadow duration-300 flex flex-col items-center justify-center">
                                <div className="w-12 h-12 flex items-center justify-center bg-white rounded-full shadow-sm mb-3">
                                    <img src="/assets/logos/trivy.png" alt="Portus Registry" className="w-8 h-8" />
                                </div>
                                <span className="font-medium text-gray-800">Trivy</span>
                                <span className="text-xs text-gray-500 mt-1">Vulnerability Scanner</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Development Tools */}
                <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100">
                    <div className="bg-gradient-to-r from-blue-600 to-indigo-700 px-6 py-4 flex items-center">
                        <div className="w-10 h-10 flex-shrink-0 bg-white rounded-full flex items-center justify-center mr-4">
                            <DashboardOutlined className="text-blue-700 text-xl" />
                        </div>
                        <h3 className="text-xl font-bold text-white">Development Tools</h3>
                    </div>

                    <div className="p-6">
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                            <div className="bg-indigo-50 rounded-lg p-4 text-center hover:shadow-md transition-shadow duration-300 flex flex-col items-center justify-center">
                                <div className="w-12 h-12 flex items-center justify-center bg-white rounded-full shadow-sm mb-3">
                                    <img src="/assets/logos/git.png" alt="Git" className="w-8 h-8" />
                                </div>
                                <span className="font-medium text-gray-800">Git</span>
                                <span className="text-xs text-gray-500 mt-1">Version Control</span>
                            </div>

                            <div className="bg-indigo-50 rounded-lg p-4 text-center hover:shadow-md transition-shadow duration-300 flex flex-col items-center justify-center">
                                <div className="w-12 h-12 flex items-center justify-center bg-white rounded-full shadow-sm mb-3">
                                    <img src="/assets/logos/intellij.png" alt="IntelliJ IDEA" className="w-8 h-8" />
                                </div>
                                <span className="font-medium text-gray-800">IntelliJ IDEA</span>
                                <span className="text-xs text-gray-500 mt-1">IDE</span>
                            </div>

                            <div className="bg-indigo-50 rounded-lg p-4 text-center hover:shadow-md transition-shadow duration-300 flex flex-col items-center justify-center">
                                <div className="w-12 h-12 flex items-center justify-center bg-white rounded-full shadow-sm mb-3">
                                    <img src="/assets/logos/pycharm.png" alt="PyCharm IDEA" className="w-8 h-8" />
                                </div>
                                <span className="font-medium text-gray-800">PyCharm IDEA</span>
                                <span className="text-xs text-gray-500 mt-1">IDE</span>
                            </div>

                            <div className="bg-indigo-50 rounded-lg p-4 text-center hover:shadow-md transition-shadow duration-300 flex flex-col items-center justify-center">
                                <div className="w-12 h-12 flex items-center justify-center bg-white rounded-full shadow-sm mb-3">
                                    <img src="/assets/logos/datagrip.jpeg" alt="DataGrip IDEA" className="w-8 h-8" />
                                </div>
                                <span className="font-medium text-gray-800">DataGrip IDEA</span>
                                <span className="text-xs text-gray-500 mt-1">IDE</span>
                            </div>

                            <div className="bg-indigo-50 rounded-lg p-4 text-center hover:shadow-md transition-shadow duration-300 flex flex-col items-center justify-center">
                                <div className="w-12 h-12 flex items-center justify-center bg-white rounded-full shadow-sm mb-3">
                                    <img src="/assets/logos/vscode.jpeg" alt="VS Code" className="w-8 h-8" />
                                </div>
                                <span className="font-medium text-gray-800">VS Code</span>
                                <span className="text-xs text-gray-500 mt-1">Code Editor</span>
                            </div>

                            <div className="bg-indigo-50 rounded-lg p-4 text-center hover:shadow-md transition-shadow duration-300 flex flex-col items-center justify-center">
                                <div className="w-12 h-12 flex items-center justify-center bg-white rounded-full shadow-sm mb-3">
                                    <img src="/assets/logos/postman.svg" alt="Postman" className="w-8 h-8" />
                                </div>
                                <span className="font-medium text-gray-800">Postman</span>
                                <span className="text-xs text-gray-500 mt-1">API Testing</span>
                            </div>

                            <div className="bg-indigo-50 rounded-lg p-4 text-center hover:shadow-md transition-shadow duration-300 flex flex-col items-center justify-center">
                                <div className="w-12 h-12 flex items-center justify-center bg-white rounded-full shadow-sm mb-3">
                                    <img src="/assets/logos/drawio.png" alt="DrawIo" className="w-8 h-8" />
                                </div>
                                <span className="font-medium text-gray-800">DrawIo</span>
                                <span className="text-xs text-gray-500 mt-1">UML Design</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Kiến trúc */}
                <div className="bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 overflow-hidden border border-gray-100">
    <div className="bg-gradient-to-r from-blue-600 to-indigo-700 px-6 py-5 flex items-center">
        <div className="w-12 h-12 flex-shrink-0 bg-white rounded-full flex items-center justify-center mr-4 shadow-md">
            <SolutionOutlined className="text-blue-600 text-xl" />
        </div>
        <h3 className="text-xl font-bold text-white">Kiến trúc hệ thống</h3>
    </div>

    <div className="p-6">
        <div className="flex flex-col items-center text-center">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-3 px-8 rounded-full text-lg font-semibold mb-6 shadow-md transform hover:scale-105 transition-transform duration-300">
                Microservices
            </div>

            <div className="mb-6 max-w-2xl text-gray-700 bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500">
                <p className="leading-relaxed">
                    SmartForms được xây dựng trên kiến trúc microservices hiện đại, cho phép phát triển
                    linh hoạt, mở rộng dễ dàng và khả năng chịu tải cao. Các service được phân tách theo
                    chức năng và giao tiếp thông qua API Gateway.
                </p>
            </div>

            {/* Điều chỉnh grid gap và chỉnh sửa padding của các thẻ */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 w-full max-w-3xl">
                <div className="bg-blue-50 rounded-lg py-2 px-3 text-blue-800 font-medium flex items-center shadow-sm hover:shadow-md transition-shadow duration-300 border-b-2 border-blue-400 whitespace-nowrap">
                    <CheckOutlined className="mr-1 text-blue-500 flex-shrink-0" /> 
                    <span>Ghép nối lỏng lẻo</span>
                </div>
                <div className="bg-green-50 rounded-lg py-2 px-3 text-green-800 font-medium flex items-center shadow-sm hover:shadow-md transition-shadow duration-300 border-b-2 border-green-400 whitespace-nowrap">
                    <CheckOutlined className="mr-1 text-green-500 flex-shrink-0" /> 
                    <span>Khả năng mở rộng cao</span>
                </div>
                <div className="bg-purple-50 rounded-lg py-2 px-3 text-purple-800 font-medium flex items-center shadow-sm hover:shadow-md transition-shadow duration-300 border-b-2 border-purple-400 whitespace-nowrap">
                    <CheckOutlined className="mr-1 text-purple-500 flex-shrink-0" /> 
                    <span>Cách ly lỗi</span>
                </div>
                <div className="bg-yellow-50 rounded-lg py-2 px-3 text-yellow-800 font-medium flex items-center shadow-sm hover:shadow-md transition-shadow duration-300 border-b-2 border-yellow-400 whitespace-nowrap">
                    <CheckOutlined className="mr-1 text-yellow-500 flex-shrink-0" /> 
                    <span>Đa dạng công nghệ</span>
                </div>
            </div>
            
            <div className="mt-8 w-full bg-gray-50 rounded-lg p-4 border-t border-dashed border-gray-200">
                <div className="flex flex-wrap justify-center gap-2">
                    <div className="bg-white rounded-full py-1 px-3 text-sm font-medium text-gray-700 shadow-sm flex items-center">
                        <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span> Spring Boot
                    </div>
                    <div className="bg-white rounded-full py-1 px-3 text-sm font-medium text-gray-700 shadow-sm flex items-center">
                        <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span> Spring Cloud
                    </div>
                    <div className="bg-white rounded-full py-1 px-3 text-sm font-medium text-gray-700 shadow-sm flex items-center">
                        <span className="w-2 h-2 bg-red-500 rounded-full mr-2"></span> Kafka
                    </div>
                    <div className="bg-white rounded-full py-1 px-3 text-sm font-medium text-gray-700 shadow-sm flex items-center">
                        <span className="w-2 h-2 bg-yellow-500 rounded-full mr-2"></span> Redis
                    </div>
                    <div className="bg-white rounded-full py-1 px-3 text-sm font-medium text-gray-700 shadow-sm flex items-center">
                        <span className="w-2 h-2 bg-purple-500 rounded-full mr-2"></span> Elasticsearch
                    </div>
                </div>
            </div>
        </div>
    </div>
</div>
            </div>
        </>
    ),
    architecture: 'Microservices',
    screenshots: [
        {
            url: '/assets/images/projects/smartforms/smartforms-intro.png',
            caption: 'Tổng quan về SmartForms',
        },
        {
            url: '/assets/images/projects/smartforms/dang-nhap.png',
            caption: 'Giao diện đăng nhập hệ thống',
        },
        {
            url: '/assets/images/projects/smartforms/dang-ky.png',
            caption: 'Giao diện đăng ký tài khoản',
        },
        {
            url: '/assets/images/projects/smartforms/danh-sach-thu-muc.png',
            caption: 'Quản lý thư mục và form',
        },
        {
            url: '/assets/images/projects/smartforms/tao-form.png',
            caption: 'Giao diện tạo form khảo sát',
        },
        {
            url: '/assets/images/projects/smartforms/tao-de-thi.png',
            caption: 'Giao diện tạo đề thi',
        },
        {
            url: '/assets/images/projects/smartforms/tao-fom-ahp.png',
            caption: 'Tạo form phân cấp thứ bậc (AHP)',
        },
        {
            url: '/assets/images/projects/smartforms/lam-form.png',
            caption: 'Giao diện làm form khảo sát',
        },
        {
            url: '/assets/images/projects/smartforms/quan-ly-chia-se.png',
            caption: 'Quản lý chia sẻ form',
        },
        {
            url: '/assets/images/projects/smartforms/quan-ly-nguoi-dung-cua-admin.png',
            caption: 'Trang quản lý người dùng cho admin',
        },
        {
            url: '/assets/images/projects/smartforms/quan-ly-nguoi-lam-bai-thi.png',
            caption: 'Quản lý người làm bài thi',
        },
        {
            url: '/assets/images/projects/smartforms/quan-ly-danh-sach-phan-hoi-form.png',
            caption: 'Quản lý danh sách phản hồi form',
        },
        {
            url: '/assets/images/projects/smartforms/thong-tin-chi-tiet-phan-hoi.png',
            caption: 'Thông tin chi tiết phản hồi form',
        },
        {
            url: '/assets/images/projects/smartforms/cham-diem-form-kiem-tra.png',
            caption: 'Giao diện chấm điểm form kiểm tra',
        },
        {
            url: '/assets/images/projects/smartforms/phan-tich-giam-sat-form-kiem-tra.png',
            caption: 'Phân tích và giám sát form kiểm tra',
        },
        // Thêm các ảnh thống kê
        {
            url: '/assets/images/projects/smartforms/thong-ke-cua-admin-ve-he-thong-1.png',
            caption: 'Thống kê tổng quan (Admin) - Bảng điều khiển',
        },
        {
            url: '/assets/images/projects/smartforms/thong-ke-cua-admin-ve-he-thong-2.png',
            caption: 'Thống kê hoạt động hệ thống - Phần 1',
        },
        {
            url: '/assets/images/projects/smartforms/thong-ke-cua-admin-ve-he-thong-3.png',
            caption: 'Thống kê hoạt động hệ thống - Phần 2',
        },
        {
            url: '/assets/images/projects/smartforms/thong-ke-cua-admin-ve-he-thong-4.png',
            caption: 'Thống kê hoạt động hệ thống - Phần 3',
        },
        {
            url: '/assets/images/projects/smartforms/thong-ke-cua-admin-ve-he-thong-5.png',
            caption: 'Thống kê hoạt động hệ thống - Phần 4',
        },
        {
            url: '/assets/images/projects/smartforms/thong-ke-cua-admin-ve-he-thong-6.png',
            caption: 'Thống kê hoạt động hệ thống - Phần 5',
        },
        {
            url: '/assets/images/projects/smartforms/thong-ke-cua-admin-ve-he-thong-7.png',
            caption: 'Thống kê hoạt động hệ thống - Phần 6',
        },
        {
            url: '/assets/images/projects/smartforms/thong-ke-cua-admin-ve-nguoi-dung-he-thong.png',
            caption: 'Thống kê người dùng của hệ thống',
        },
        {
            url: '/assets/images/projects/smartforms/thong-ke-cua-admin-ve-so-phien-dang-nhap-cua-nguoi-dung.png',
            caption: 'Thống kê số phiên đăng nhập của người dùng',
        },
        {
            url: '/assets/images/projects/smartforms/thong-ke-cua-admin-ve-form-nguoi-dung.png',
            caption: 'Thống kê form người dùng',
        },
        {
            url: '/assets/images/projects/smartforms/thong-ke-cua-admin-ve-thi-cu-cua-nguoi-dung.png',
            caption: 'Thống kê thi cử của người dùng',
        },
        {
            url: '/assets/images/projects/smartforms/thong-ke-cua-admin-ve-cua-hang-cua-nguoi-dung.png',
            caption: 'Thống kê cửa hàng của người dùng',
        },
        {
            url: '/assets/images/projects/smartforms/thong-ke-cua-client-1.png',
            caption: 'Thống kê cá nhân - Tổng quan',
        },
        {
            url: '/assets/images/projects/smartforms/thong-ke-cua-client-2.png',
            caption: 'Thống kê cá nhân - Chi tiết hoạt động',
        },
        {
            url: '/assets/images/projects/smartforms/thong-ke-cua-client-3.png',
            caption: 'Thống kê cá nhân - Phân tích phản hồi',
        },
        {
            url: '/assets/images/projects/smartforms/thong-ke-cua-client-4.png',
            caption: 'Thống kê cá nhân - Báo cáo tổng hợp',
        },
        // Thêm các ảnh thống kê form AHP
        {
            url: '/assets/images/projects/smartforms/thong-ke-ket-qua-phan-hoi-form-ahp-1.png',
            caption: 'Kết quả phân tích form AHP - biểu đồ',
        },
        {
            url: '/assets/images/projects/smartforms/thong-ke-ket-qua-phan-hoi-form-ahp-2.png',
            caption: 'Kết quả phân tích form AHP - ma trận',
        },
        {
            url: '/assets/images/projects/smartforms/thong-ke-ket-qua-phan-hoi-form-ahp-3.png',
            caption: 'Kết quả phân tích form AHP - trọng số',
        },
        // Thêm các ảnh thống kê form kiểm tra
        {
            url: '/assets/images/projects/smartforms/thong-ke-phan-hoi-form-kiem-tra-1.png',
            caption: 'Phân tích kết quả kiểm tra - điểm số',
        },
        {
            url: '/assets/images/projects/smartforms/thong-ke-phan-hoi-form-kiem-tra-2.png',
            caption: 'Phân tích kết quả kiểm tra - phân phối',
        },
        {
            url: '/assets/images/projects/smartforms/thong-ke-phan-hoi-form-kiem-tra-3.png',
            caption: 'Phân tích kết quả kiểm tra - từng câu hỏi',
        },
        {
            url: '/assets/images/projects/smartforms/thong-ke-phan-hoi-form-kiem-tra-4.png',
            caption: 'Phân tích kết quả kiểm tra - tổng hợp',
        },
    ],
    results: () => (
        <>
            <h2 className="text-2xl font-bold mb-4">Kết quả đạt được</h2>
            <p className="mb-4">SmartForms đã đạt được nhiều kết quả đáng kể:</p>

            <div className="mb-6 space-y-4">
                <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500">
                    <h3 className="text-lg font-semibold text-blue-700 mb-2">Hệ thống toàn diện</h3>
                    <p className="text-gray-700">
                        Đã xây dựng thành công một nền tảng form đa năng với nhiều loại form khác nhau đáp ứng nhiều nhu
                        cầu từ khảo sát, thi cử đến bán hàng và điểm danh.
                    </p>
                </div>

                <div className="bg-green-50 p-4 rounded-lg border-l-4 border-green-500">
                    <h3 className="text-lg font-semibold text-green-700 mb-2">Tích hợp thành công nhiều công nghệ</h3>
                    <p className="text-gray-700">
                        Kết hợp hiệu quả các công nghệ hiện đại từ microservices, AI nhận diện khuôn mặt, đến xử lý dữ
                        liệu thời gian thực với Kafka và WebSocket.
                    </p>
                </div>

                <div className="bg-purple-50 p-4 rounded-lg border-l-4 border-purple-500">
                    <h3 className="text-lg font-semibold text-purple-700 mb-2">Bảo mật cao cấp</h3>
                    <p className="text-gray-700">
                        Triển khai nhiều lớp bảo mật, từ mã hóa dữ liệu, JWT/OAuth2 đến CI/CD tích hợp công cụ quét bảo
                        mật Snyk và Trivy.
                    </p>
                </div>

                <div className="bg-yellow-50 p-4 rounded-lg border-l-4 border-yellow-500">
                    <h3 className="text-lg font-semibold text-yellow-700 mb-2">Khả năng mở rộng</h3>
                    <p className="text-gray-700">
                        Kiến trúc microservices và CI/CD pipeline hoàn chỉnh với GitLab, Docker, và Kubernetes cho phép
                        hệ thống dễ dàng mở rộng.
                    </p>
                </div>

                <div className="bg-indigo-50 p-4 rounded-lg border-l-4 border-indigo-500">
                    <h3 className="text-lg font-semibold text-indigo-700 mb-2">Phản hồi tích cực từ người dùng</h3>
                    <p className="text-gray-700">
                        Trong quá trình thử nghiệm, người dùng đánh giá cao tính đa dạng, dễ sử dụng và độ tin cậy của
                        hệ thống.
                    </p>
                </div>

                <div className="bg-red-50 p-4 rounded-lg border-l-4 border-red-500">
                    <h3 className="text-lg font-semibold text-red-700 mb-2">Triển khai DevSecOps thành công</h3>
                    <p className="text-gray-700">
                        Xây dựng quy trình CI/CD hoàn chỉnh với GitLab, tích hợp kiểm thử, quét bảo mật và giám sát hiệu
                        suất.
                    </p>
                </div>
            </div>

            <p className="mb-4">
                SmartForms không chỉ đáp ứng các yêu cầu của đồ án tốt nghiệp mà còn thể hiện tiềm năng thực tế cao, sẵn
                sàng cho việc triển khai và sử dụng trong nhiều tình huống khác nhau.
            </p>
        </>
    ),
    challenges: () => (
        <>
            <h2 className="text-2xl font-bold mb-4">Thách thức</h2>
            <p className="mb-4">Trong quá trình phát triển SmartForms, tôi đã gặp và vượt qua một số thách thức:</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-lg shadow-sm border-l-4 border-blue-500 hover:shadow-md transition-shadow">
                    <h3 className="text-lg font-semibold mb-2 text-blue-800">
                        Thiết kế kiến trúc microservices phức tạp
                    </h3>
                    <p className="text-gray-700">
                        Việc phân chia các service, xác định ranh giới và đảm bảo giao tiếp hiệu quả giữa các service
                        đòi hỏi nhiều nỗ lực thiết kế và lập trình.
                    </p>
                </div>

                <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-4 rounded-lg shadow-sm border-l-4 border-purple-500 hover:shadow-md transition-shadow">
                    <h3 className="text-lg font-semibold mb-2 text-purple-800">Tích hợp đồng bộ nhiều công nghệ</h3>
                    <p className="text-gray-700">
                        Kết hợp hài hòa các công nghệ khác nhau như Spring Boot, React, Kafka, Redis, ElasticSearch và
                        nhiều công nghệ khác là một thách thức lớn.
                    </p>
                </div>

                <div className="bg-gradient-to-r from-green-50 to-teal-50 p-4 rounded-lg shadow-sm border-l-4 border-green-500 hover:shadow-md transition-shadow">
                    <h3 className="text-lg font-semibold mb-2 text-green-800">Bảo mật đa lớp</h3>
                    <p className="text-gray-700">
                        Triển khai hệ thống bảo mật đa lớp với OAuth2, JWT, mã hóa dữ liệu và kiểm tra tính toàn vẹn là
                        một công việc đòi hỏi sự cẩn trọng và kỹ lưỡng.
                    </p>
                </div>

                <div className="bg-gradient-to-r from-yellow-50 to-amber-50 p-4 rounded-lg shadow-sm border-l-4 border-yellow-500 hover:shadow-md transition-shadow">
                    <h3 className="text-lg font-semibold mb-2 text-yellow-800">Xử lý thời gian thực</h3>
                    <p className="text-gray-700">
                        Đảm bảo cập nhật thời gian thực qua WebSocket, đặc biệt là trong các tình huống tải cao, đòi hỏi
                        tối ưu hóa đáng kể.
                    </p>
                </div>

                <div className="bg-gradient-to-r from-red-50 to-rose-50 p-4 rounded-lg shadow-sm border-l-4 border-red-500 hover:shadow-md transition-shadow">
                    <h3 className="text-lg font-semibold mb-2 text-red-800">Tích hợp nhận diện khuôn mặt</h3>
                    <p className="text-gray-700">
                        Triển khai và tối ưu hóa hệ thống nhận diện khuôn mặt để đảm bảo độ chính xác và hiệu suất cao
                        là một thách thức kỹ thuật lớn.
                    </p>
                </div>

                <div className="bg-gradient-to-r from-cyan-50 to-sky-50 p-4 rounded-lg shadow-sm border-l-4 border-cyan-500 hover:shadow-md transition-shadow">
                    <h3 className="text-lg font-semibold mb-2 text-cyan-800">Xây dựng pipeline CI/CD hoàn chỉnh</h3>
                    <p className="text-gray-700">
                        Thiết lập pipeline CI/CD đầy đủ với kiểm thử, quét bảo mật và triển khai tự động đòi hỏi kiến
                        thức sâu về DevOps.
                    </p>
                </div>

                <div className="bg-gradient-to-r from-orange-50 to-amber-50 p-4 rounded-lg shadow-sm border-l-4 border-orange-500 hover:shadow-md transition-shadow col-span-1 md:col-span-2">
                    <h3 className="text-lg font-semibold mb-2 text-orange-800">
                        Đảm bảo hiệu suất với lượng dữ liệu lớn
                    </h3>
                    <p className="text-gray-700">
                        Tối ưu hóa hiệu suất hệ thống, đặc biệt là với các form có lượng lớn phản hồi hoặc các đề thi có
                        nhiều người làm đồng thời.
                    </p>
                </div>
            </div>
        </>
    ),
    learnings: () => (
        <>
            <h2 className="text-2xl font-bold mb-4">Bài học kinh nghiệm</h2>
            <p className="mb-4">Dự án này đã cung cấp nhiều bài học quý giá:</p>

            <div className="space-y-6 mb-6">
                <div className="flex items-start">
                    <div className="bg-blue-100 text-blue-800 p-2 rounded-full mr-4 mt-1">1</div>
                    <div>
                        <h3 className="text-lg font-semibold mb-2">Kiến trúc microservices trong thực tế</h3>
                        <p className="text-gray-700">
                            Tôi đã học được cách thiết kế, triển khai và vận hành một hệ thống microservices thực tế,
                            bao gồm cách xử lý các vấn đề phổ biến như giao tiếp giữa các service, đảm bảo tính nhất
                            quán dữ liệu và xử lý lỗi.
                        </p>
                    </div>
                </div>

                <div className="flex items-start">
                    <div className="bg-green-100 text-green-800 p-2 rounded-full mr-4 mt-1">2</div>
                    <div>
                        <h3 className="text-lg font-semibold mb-2">Tích hợp đa nền tảng</h3>
                        <p className="text-gray-700">
                            Kinh nghiệm phát triển một hệ thống end-to-end, từ frontend đến backend và cơ sở dữ liệu, đã
                            giúp tôi hiểu sâu về cách các thành phần khác nhau tương tác với nhau.
                        </p>
                    </div>
                </div>

                <div className="flex items-start">
                    <div className="bg-red-100 text-red-800 p-2 rounded-full mr-4 mt-1">3</div>
                    <div>
                        <h3 className="text-lg font-semibold mb-2">DevSecOps trong thực tế</h3>
                        <p className="text-gray-700">
                            Thiết lập và vận hành pipeline CI/CD, tích hợp kiểm thử và quét bảo mật đã cung cấp kinh
                            nghiệm thực tế quý báu về DevSecOps.
                        </p>
                    </div>
                </div>

                <div className="flex items-start">
                    <div className="bg-yellow-100 text-yellow-800 p-2 rounded-full mr-4 mt-1">4</div>
                    <div>
                        <h3 className="text-lg font-semibold mb-2">Bảo mật hệ thống</h3>
                        <p className="text-gray-700">
                            Tôi đã học được nhiều kỹ thuật bảo mật khác nhau, từ mã hóa dữ liệu, xác thực và ủy quyền
                            đến quét lỗ hổng bảo mật và bảo vệ dữ liệu người dùng.
                        </p>
                    </div>
                </div>

                <div className="flex items-start">
                    <div className="bg-purple-100 text-purple-800 p-2 rounded-full mr-4 mt-1">5</div>
                    <div>
                        <h3 className="text-lg font-semibold mb-2">Xử lý dữ liệu thời gian thực</h3>
                        <p className="text-gray-700">
                            Kinh nghiệm với Kafka và WebSocket đã dạy tôi cách xử lý dữ liệu thời gian thực hiệu quả và
                            đáng tin cậy.
                        </p>
                    </div>
                </div>

                <div className="flex items-start">
                    <div className="bg-indigo-100 text-indigo-800 p-2 rounded-full mr-4 mt-1">6</div>
                    <div>
                        <h3 className="text-lg font-semibold mb-2">Tối ưu hóa hiệu suất</h3>
                        <p className="text-gray-700">
                            Tôi đã học được nhiều kỹ thuật tối ưu hóa hiệu suất, từ caching với Redis, indexing với
                            ElasticSearch đến tối ưu hóa truy vấn cơ sở dữ liệu.
                        </p>
                    </div>
                </div>

                <div className="flex items-start">
                    <div className="bg-teal-100 text-teal-800 p-2 rounded-full mr-4 mt-1">7</div>
                    <div>
                        <h3 className="text-lg font-semibold mb-2">Kỹ năng quản lý dự án</h3>
                        <p className="text-gray-700">
                            Quản lý một dự án phức tạp với nhiều thành phần và công nghệ đã cải thiện đáng kể kỹ năng
                            quản lý dự án, ước tính thời gian và giải quyết vấn đề của tôi.
                        </p>
                    </div>
                </div>
            </div>
        </>
    ),
    conclusion: () => (
        <>
            <h2 className="text-2xl font-bold mb-4">Kết luận và Hướng phát triển</h2>
<div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg shadow-md mb-6 border-l-4 border-blue-500">
    <h3 className="text-xl font-semibold text-gray-800 mb-3">Kết luận</h3>
    <p className="mb-4 text-gray-800 leading-relaxed">
        Trong quá trình thực hiện Đồ án tốt nghiệp, SmartForms đã được phát triển thành công với mục tiêu tạo ra một nền tảng quản lý form đa năng, tích hợp nhiều tính năng phục vụ cho các mục đích khác nhau như thu thập dữ liệu, tổ chức thi cử, bán hàng, điểm danh, nhận diện khuôn mặt và phân cấp thứ bậc.
    </p>
    <p className="mb-4 text-gray-800 leading-relaxed">
        Kết quả đạt được là một hệ thống hoạt động ổn định, có khả năng tùy biến cao và tích hợp nhiều công nghệ hiện đại như React, Spring Boot, Kafka, Redis, và Flask. So với các sản phẩm tương tự trên thị trường, SmartForms nổi bật nhờ tính linh hoạt trong việc quản lý và tùy chỉnh các loại form, cũng như khả năng tích hợp các công nghệ AI như nhận diện khuôn mặt và xử lý dữ liệu thời gian thực.
    </p>
    <p className="mb-4 text-gray-800 leading-relaxed">
        Tuy nhiên, vẫn còn một số hạn chế cần khắc phục, chẳng hạn như việc tối ưu hóa hiệu suất cho các tác vụ xử lý hình ảnh lớn và mở rộng khả năng hỗ trợ đa ngôn ngữ.
    </p>
    <p className="text-gray-800 leading-relaxed">
        Trong suốt quá trình thực hiện, nhiều kiến thức và kỹ năng quan trọng đã được học hỏi, từ việc thiết kế kiến trúc hệ thống, tích hợp các công nghệ, đến quản lý dự án và giải quyết các vấn đề phát sinh. Bài học kinh nghiệm lớn nhất rút ra từ quá trình này là tầm quan trọng của việc lập kế hoạch chi tiết, quản lý thời gian hiệu quả, và thường xuyên kiểm tra, đánh giá chất lượng từng thành phần để hạn chế lỗi và đảm bảo tiến độ.
    </p>
</div>

<div className="bg-gradient-to-r from-green-50 to-teal-50 p-6 rounded-lg shadow-md mb-6 border-l-4 border-green-500">
    <h3 className="text-xl font-semibold text-gray-800 mb-3">Hướng phát triển</h3>
    <p className="mb-4 text-gray-800 leading-relaxed">
        Để hoàn thiện và nâng cấp hệ thống SmartForms, một số công việc dự kiến sẽ được thực hiện trong tương lai:
    </p>

    <div className="mb-4">
        <h4 className="font-semibold text-gray-800 mb-2">1. Hoàn thiện các chức năng hiện có:</h4>
        <ul className="list-disc pl-6 space-y-1 text-gray-800">
            <li>Tối ưu hóa hiệu suất của các dịch vụ AI, đặc biệt là nhận diện khuôn mặt, để xử lý được lượng lớn dữ liệu hình ảnh trong thời gian ngắn.</li>
            <li>Mở rộng khả năng hỗ trợ đa ngôn ngữ, giúp hệ thống có thể tiếp cận được với nhiều người dùng trên các quốc gia khác nhau.</li>
            <li>Cải thiện giao diện người dùng để tăng tính thân thiện và dễ sử dụng.</li>
        </ul>
    </div>

    <div className="mb-4">
        <h4 className="font-semibold text-gray-800 mb-2">2. Phát triển các tính năng mới:</h4>
        <ul className="list-disc pl-6 space-y-1 text-gray-800">
            <li>Cải thiện hiệu năng cơ sở dữ liệu bằng cơ chế replication database để phân tải và tăng hiệu suất xử lý.</li>
            <li>AI sinh form tự động dựa trên prompt của người dùng, giúp tiết kiệm thời gian và tăng tính linh hoạt.</li>
            <li>Quản lý quyền tốt hơn bằng cách áp dụng các mô hình tiên tiến như ACL, RBAC, hoặc ABAC với sự hỗ trợ của Casbin.</li>
        </ul>
    </div>

    <div>
        <h4 className="font-semibold text-gray-800 mb-2">3. Mở rộng khả năng tích hợp và triển khai:</h4>
        <ul className="list-disc pl-6 space-y-1 text-gray-800">
            <li>Tích hợp với các nền tảng đám mây như AWS, Google Cloud hoặc Azure để tăng khả năng mở rộng và độ tin cậy của hệ thống.</li>
            <li>Phát triển các phiên bản ứng dụng di động (iOS và Android) để người dùng có thể truy cập và sử dụng hệ thống một cách thuận tiện hơn.</li>
        </ul>
    </div>
</div>

<div className="flex flex-col items-center mt-8">
    <div className="bg-white p-5 rounded-xl shadow-lg w-full max-w-3xl mb-4">
        <h3 className="text-xl font-semibold text-center text-blue-700 mb-3">
            Hành trình phát triển SmartForms: Từ ý tưởng đến sản phẩm
        </h3>
        <div className="flex justify-center">
            <img
                src="/assets/images/projects/smartforms/smartforms-intro.png"
                alt="SmartForms"
                className="rounded-lg shadow-md w-full max-w-2xl hover:shadow-xl transition-shadow duration-300"
            />
        </div>
        <p className="text-sm text-center text-gray-600 italic mt-3">
            SmartForms - Hệ thống quản lý form đa năng với kiến trúc microservices và công nghệ hiện đại
        </p>
    </div>
</div>
        </>
    ),
    teamSize: 1,
    duration: '6 tháng',
    role: 'Full Stack Developer',
    responsibilities: [
        'Thiết kế và triển khai kiến trúc microservices',
        'Phát triển frontend với ReactJS, Redux và Ant Design',
        'Phát triển backend với Spring Boot, Spring Webflux, Spring Security',
        'Tích hợp nhận diện khuôn mặt sử dụng Python và Flask',
        'Triển khai hệ thống thông báo thời gian thực với WebSocket, Kafka và FCM',
        'Thiết kế và triển khai cơ sở dữ liệu MySQL, Redis và Elasticsearch',
        'Xây dựng pipeline CI/CD với GitLab, Docker và Kubernetes',
        'Tích hợp kiểm thử, quét bảo mật và giám sát hiệu suất',
        'Tối ưu hóa hiệu suất và bảo mật hệ thống',
    ],
};
